import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  User, Shield, CreditCard, FileText, HelpCircle, Save, Eye, EyeOff,
  Lock, Key, Smartphone, CheckCircle, AlertCircle, Download, Copy,
  Building2, Users, Target, Scan, FileCheck, Loader2, ChevronRight,
  Crown, Zap, Server, Globe, RefreshCw, Camera, Languages, FileSignature, Upload, Image,
  ClipboardList, History, Info, UserPlus, Mail, MoreVertical, Trash2, Send,
  Wallet, Package, Coins, LayoutDashboard, ShoppingCart, Gift, Clock, Calendar,
  ArrowUpRight, ArrowDownRight, Receipt, Check, Share2, Award, TrendingUp,
  Link2, ExternalLink, Trophy, Bell, Plug, Search, X, Sparkles
} from 'lucide-react';
import { toast } from '../../utils/toastUtils';
import { account, billing, workspaces as workspacesApi, credits as creditsApi, historyActions } from '../../services/api/client';
import { auth } from '../../api/client';
import { useCredits, useCreditPlans, useCreditPacks, useCreditTransactions, useCreditCosts } from '../../hooks/useCredits';
import { useI18n, LANGUAGES } from '../../i18n';
import {
  useReferralDashboard,
  useReferralContacts,
  useInviteContact,
  useResendInvitation,
  useDeleteContact,
  useReferralLeaderboard,
  useReferralRewards,
  useReferralLink
} from '../../hooks/useReferral';

// Integration Wizards
import SplunkWizard from '../../components/wizards/SplunkWizard';
import JiraWizard from '../../components/wizards/JiraWizard';
import EmailWizard from '../../components/wizards/EmailWizard';
import GenericWebhookWizard from '../../components/wizards/GenericWebhookWizard';
import IntegrationManualConfigModal from '../../components/IntegrationManualConfigModal';
import ContractManager from '../../components/ContractManager';
import { ReportTypesPanel } from '../Reports/ReportTypes';

// Plan badge colors and icons
const PLAN_STYLES = {
  free: { color: 'text-gray-400', bg: 'bg-gray-500/10', border: 'border-gray-500/30', icon: Globe },
  pro_audit: { color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30', icon: Zap },
  pro_monthly: { color: 'text-brand-green', bg: 'bg-brand-green/10', border: 'border-brand-green/30', icon: Crown },
  enterprise: { color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30', icon: Building2 },
  dedicated: { color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30', icon: Server }
};

// LANGUAGES is now imported from i18n context

// ============================================
// CREDITS TAB COMPONENTS
// ============================================

// Credits Dashboard Tab
const CreditsTab = () => {
  const { summary, loading } = useCredits();
  const { costs, loading: costsLoading } = useCreditCosts();

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <Loader2 className="animate-spin text-brand-green" size={32} />
      </div>
    );
  }

  const totalCredits = summary?.total_credits || 0;
  const monthlyCredits = summary?.monthly_credits || 0;
  const packCredits = summary?.pack_credits || 0;

  // Group costs by category
  const costsByCategory = costs?.reduce((acc, cost) => {
    if (!acc[cost.category]) acc[cost.category] = [];
    acc[cost.category].push(cost);
    return acc;
  }, {}) || {};

  const categoryLabels = {
    scan: 'Scans',
    leak: 'Détection de fuites',
    audit: 'Audits',
    report: 'Rapports'
  };

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
        <Wallet className="w-5 h-5 text-brand-green" />
        Gestion des Credits
      </h2>

      {/* Main Balance */}
      <div className="bg-[#0d0d0d] rounded-xl p-6 border border-brand-green/30">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-xl bg-brand-green/20 flex items-center justify-center flex-shrink-0">
            <Wallet className="w-7 h-7 text-brand-green" />
          </div>
          <div className="min-w-0">
            <h3 className="text-gray-400 text-sm">Solde total disponible</h3>
            <div className="text-4xl font-bold text-white">{totalCredits.toLocaleString('fr-FR')}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Monthly Credits */}
          <div className="bg-[#111111] rounded-xl p-4 border border-gray-800 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-brand-green flex-shrink-0" />
              <span className="text-gray-400 text-sm truncate">Mensuels</span>
            </div>
            <div className="text-2xl font-bold text-white">{monthlyCredits.toLocaleString('fr-FR')}</div>
            {summary?.monthly_credits_expire_at && (
              <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                <Clock className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">Expire: {new Date(summary.monthly_credits_expire_at).toLocaleDateString('fr-FR')}</span>
              </div>
            )}
          </div>

          {/* Pack Credits */}
          <div className="bg-[#111111] rounded-xl p-4 border border-gray-800 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-4 h-4 text-purple-400 flex-shrink-0" />
              <span className="text-gray-400 text-sm truncate">Packs</span>
            </div>
            <div className="text-2xl font-bold text-white">{packCredits.toLocaleString('fr-FR')}</div>
            <div className="text-xs text-gray-500 mt-1">Sans expiration</div>
          </div>
        </div>
      </div>

      {/* Current Plan */}
      {summary?.subscription_plan_name && (
        <div className="bg-[#0d0d0d] rounded-xl p-4 border border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crown className="w-5 h-5 text-brand-green" />
            <div>
              <span className="text-gray-400 text-sm">Plan actuel: </span>
              <span className="font-semibold text-white">{summary.subscription_plan_name}</span>
            </div>
          </div>
          {summary.subscription_status && (
            <span className={`text-xs px-3 py-1.5 rounded-full ${
              summary.subscription_status === 'active'
                ? 'bg-brand-green/20 text-brand-green border border-brand-green/30'
                : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
            }`}>
              {summary.subscription_status === 'active' ? 'Actif' : summary.subscription_status}
            </span>
          )}
        </div>
      )}

      {/* Costs Section */}
      {!costsLoading && Object.keys(costsByCategory).length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Coins className="w-5 h-5 text-brand-green" />
            Coûts en crédits
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {Object.entries(costsByCategory).map(([category, items]) => (
              <div key={category} className="bg-[#0d0d0d] rounded-xl p-4 border border-gray-800">
                <h4 className="font-semibold mb-3 text-white flex items-center gap-2">
                  <Zap className="w-4 h-4 text-brand-green" />
                  {categoryLabels[category] || category}
                </h4>
                <div className="space-y-2">
                  {items.map((cost) => (
                    <div key={cost.action_type} className="flex justify-between items-center py-2 border-b border-gray-800 last:border-0">
                      <div>
                        <div className="font-medium text-sm text-white">{cost.action_name}</div>
                        {cost.description && (
                          <div className="text-xs text-gray-500">{cost.description}</div>
                        )}
                      </div>
                      <div className="font-bold text-brand-green">{cost.credits_required} cr.</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Credits Plans & Packs Tab
const CreditsPlansTab = () => {
  const { summary, refresh: refreshCredits } = useCredits();
  const { plans, loading: plansLoading } = useCreditPlans();
  const { packs, loading: packsLoading } = useCreditPacks();
  const [dialog, setDialog] = useState({ open: false, item: null, type: null });
  const [processing, setProcessing] = useState(false);

  const handleConfirm = async () => {
    const { item, type } = dialog;
    setProcessing(true);

    try {
      if (type === 'plan') {
        await creditsApi.subscribe(item.slug);
        toast.success(`Vous avez souscrit au plan ${item.name}`);
      } else {
        await creditsApi.purchasePack(item.slug);
        toast.success(`Pack ${item.name} achete avec succes !`);
      }
      await refreshCredits();
      setDialog({ open: false, item: null, type: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="p-6 space-y-8">
      {/* Plans Section */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
          <Crown className="w-5 h-5 text-brand-green" />
          Plans mensuels
        </h2>

        {plansLoading ? (
          <div className="flex justify-center p-8">
            <Loader2 className="animate-spin text-brand-green" size={32} />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {plans?.map((plan) => {
              const isCurrentPlan = summary?.subscription_plan_slug === plan.slug;
              return (
                <div
                  key={plan.slug}
                  className={`bg-[#0d0d0d] rounded-xl p-6 border-2 transition-all ${
                    isCurrentPlan
                      ? 'border-brand-green/50 bg-brand-green/5'
                      : 'border-gray-800 hover:border-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-brand-green/20 flex items-center justify-center">
                      <Crown className="w-5 h-5 text-brand-green" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-white">{plan.name}</h4>
                      {isCurrentPlan && (
                        <span className="text-xs text-brand-green">Plan actuel</span>
                      )}
                    </div>
                  </div>

                  <div className="text-3xl font-bold text-white mb-2">
                    {plan.monthly_price_eur === 0 ? 'Gratuit' : `${plan.monthly_price_eur}€`}
                    {plan.monthly_price_eur > 0 && <span className="text-sm font-normal text-gray-400">/mois</span>}
                  </div>

                  <div className="text-lg mb-4">
                    <span className="font-semibold text-brand-green">{plan.monthly_credits?.toLocaleString('fr-FR') || 0}</span>
                    <span className="text-gray-400"> credits/mois</span>
                  </div>

                  <ul className="text-sm text-gray-300 space-y-2 mb-6">
                    <li className="flex items-center gap-2">
                      <Check size={16} className="text-brand-green" />
                      {plan.max_workspaces === -1 ? 'Workspaces illimites' : `${plan.max_workspaces} workspace${plan.max_workspaces > 1 ? 's' : ''}`}
                    </li>
                    {plan.features?.scans_externes && (
                      <li className="flex items-center gap-2">
                        <Check size={16} className="text-brand-green" />
                        Scans externes
                      </li>
                    )}
                    {plan.features?.scans_internes && (
                      <li className="flex items-center gap-2">
                        <Check size={16} className="text-brand-green" />
                        Scans internes
                      </li>
                    )}
                  </ul>

                  <button
                    onClick={() => setDialog({ open: true, item: plan, type: 'plan' })}
                    disabled={isCurrentPlan}
                    className={`w-full py-3 rounded-xl font-medium transition-all ${
                      isCurrentPlan
                        ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                        : 'bg-brand-green text-black hover:bg-brand-green/90'
                    }`}
                  >
                    {isCurrentPlan ? 'Plan actuel' : 'Selectionner'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Packs Section */}
      <div>
        <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
          <Package className="w-5 h-5 text-purple-400" />
          Packs de credits
        </h3>

        {packsLoading ? (
          <div className="flex justify-center p-8">
            <Loader2 className="animate-spin text-brand-green" size={32} />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {packs?.map((pack) => (
              <div key={pack.slug} className="bg-[#0d0d0d] rounded-xl p-6 border border-gray-800 hover:border-purple-500/30 transition-all">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-purple-400" />
                  </div>
                  <h4 className="text-lg font-bold text-white">{pack.name}</h4>
                </div>

                <div className="text-3xl font-bold text-purple-400 mb-2">
                  {pack.credits?.toLocaleString('fr-FR') || 0} <span className="text-sm font-normal text-gray-400">credits</span>
                </div>

                <div className="text-xl font-semibold text-white mb-2">
                  {pack.price_eur?.toLocaleString('fr-FR') || 0}€
                  {pack.discount_percentage > 0 && (
                    <span className="ml-2 text-sm bg-brand-green/20 text-brand-green px-2 py-1 rounded-full">
                      -{pack.discount_percentage}%
                    </span>
                  )}
                </div>

                <p className="text-sm text-gray-400 mb-4">{pack.description}</p>

                <div className="text-xs text-gray-500 mb-4 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Validite: {pack.validity_months} mois
                </div>

                <button
                  onClick={() => setDialog({ open: true, item: pack, type: 'pack' })}
                  className="w-full py-3 bg-purple-500/20 text-purple-400 rounded-xl font-medium hover:bg-purple-500/30 transition-all border border-purple-500/30"
                >
                  <ShoppingCart className="w-4 h-4 inline mr-2" />
                  Acheter
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {dialog.open && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-[#0d0d0d] rounded-xl border border-brand-green/30 max-w-md w-full mx-4 p-6">
            <h3 className="text-xl font-bold text-white mb-4">
              {dialog.type === 'plan' ? 'Confirmer le changement de plan' : "Confirmer l'achat"}
            </h3>

            {dialog.item && (
              <div className="mb-6">
                <div className="text-lg font-semibold text-white">{dialog.item.name}</div>
                <div className="text-2xl font-bold text-brand-green my-2">
                  {dialog.type === 'plan'
                    ? `${dialog.item.monthly_credits?.toLocaleString('fr-FR') || 0} credits/mois`
                    : `${dialog.item.credits?.toLocaleString('fr-FR') || 0} credits`
                  }
                </div>
                <div className="text-lg text-white">
                  {dialog.type === 'plan'
                    ? (dialog.item.monthly_price_eur === 0 ? 'Gratuit' : `${dialog.item.monthly_price_eur}€/mois`)
                    : `${dialog.item.price_eur?.toLocaleString('fr-FR') || 0}€`
                  }
                </div>
              </div>
            )}

            <div className="bg-brand-green/10 border border-brand-green/30 rounded-xl p-3 mb-6 text-sm text-brand-green">
              {dialog.type === 'plan'
                ? 'Votre nouveau plan sera actif immédiatement.'
                : 'Les crédits seront ajoutés immédiatement à votre compte.'
              }
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setDialog({ open: false, item: null, type: null })}
                disabled={processing}
                className="flex-1 py-3 border border-gray-700 rounded-xl text-gray-300 hover:bg-gray-800/50 disabled:opacity-50 transition-all"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirm}
                disabled={processing}
                className="flex-1 py-3 bg-brand-green text-black rounded-xl hover:bg-brand-green/90 disabled:opacity-50 flex items-center justify-center gap-2 transition-all font-medium"
              >
                {processing && <Loader2 className="animate-spin" size={16} />}
                {processing ? 'Traitement...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Credits History Tab
const CreditsHistoryTab = () => {
  const { transactions, loading, refresh } = useCreditTransactions({ limit: 100 });

  const isPositive = (type) => {
    return ['credit_monthly_renewal', 'credit_pack_purchase', 'credit_bonus', 'credit_refund'].includes(type);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Receipt className="w-5 h-5 text-brand-green" />
          Historique des transactions
        </h2>
        <button
          onClick={refresh}
          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800/50 rounded-lg transition-all"
        >
          <RefreshCw size={14} />
          Actualiser
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center p-8">
          <Loader2 className="animate-spin text-brand-green" size={32} />
        </div>
      ) : transactions?.length === 0 ? (
        <div className="bg-[#0d0d0d] rounded-xl p-8 border border-gray-800 text-center">
          <History className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">Aucune transaction pour le moment</p>
        </div>
      ) : (
        <div className="bg-[#0d0d0d] rounded-xl border border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#111111]">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Type</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Montant</th>
                </tr>
              </thead>
              <tbody>
                {transactions?.map((tx) => (
                  <tr key={tx.id} className="border-t border-gray-800 hover:bg-gray-800/30">
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {tx.created_at ? new Date(tx.created_at).toLocaleString('fr-FR') : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isPositive(tx.transaction_type) ? (
                          <ArrowUpRight className="w-4 h-4 text-brand-green" />
                        ) : (
                          <ArrowDownRight className="w-4 h-4 text-red-400" />
                        )}
                        <span className={`text-sm px-2 py-1 rounded-lg ${
                          isPositive(tx.transaction_type)
                            ? 'bg-brand-green/10 text-brand-green'
                            : 'bg-red-500/10 text-red-400'
                        }`}>
                          {tx.transaction_type.replace('credit_', '').replace(/_/g, ' ')}
                        </span>
                      </div>
                    </td>
                    <td className={`px-4 py-3 text-right font-bold ${
                      isPositive(tx.transaction_type) ? 'text-brand-green' : 'text-red-400'
                    }`}>
                      {isPositive(tx.transaction_type) ? '+' : ''}{tx.credits_amount?.toLocaleString('fr-FR') || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// END CREDITS TAB COMPONENTS
// ============================================

// ============================================
// REFERRAL TAB COMPONENT
// ============================================

const TIER_STYLES = {
  bronze: { bg: 'bg-amber-700/20', border: 'border-amber-600/30', text: 'text-amber-500', icon: '🥉' },
  silver: { bg: 'bg-gray-400/20', border: 'border-gray-400/30', text: 'text-gray-300', icon: '🥈' },
  gold: { bg: 'bg-yellow-500/20', border: 'border-yellow-500/30', text: 'text-yellow-400', icon: '🥇' },
  platinum: { bg: 'bg-cyan-400/20', border: 'border-cyan-400/30', text: 'text-cyan-400', icon: '💎' }
};

const STATUS_STYLES = {
  invited: { bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Invite' },
  clicked: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', label: 'A clique' },
  signed_up: { bg: 'bg-brand-green/10', text: 'text-brand-green', label: 'Inscrit' },
  converted: { bg: 'bg-purple-500/10', text: 'text-purple-400', label: 'Converti' },
  churned: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Parti' }
};

const ReferralTab = () => {
  const { stats, loading: statsLoading, refetch: refetchStats } = useReferralDashboard();
  const { contacts, loading: contactsLoading, refetch: refetchContacts } = useReferralContacts();
  const { leaderboard, loading: leaderboardLoading } = useReferralLeaderboard(5);
  const { linkData } = useReferralLink();
  const { invite, loading: inviteLoading } = useInviteContact();
  const { resend } = useResendInvitation();
  const { deleteContact } = useDeleteContact();

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    company_name: ''
  });
  const [activeSection, setActiveSection] = useState('dashboard');

  const copyReferralLink = () => {
    if (linkData?.referral_link) {
      navigator.clipboard.writeText(linkData.referral_link);
      toast.success('Lien de parrainage copié !');
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      await invite(inviteForm);
      toast.success('Invitation envoyée avec succès !');
      setShowInviteModal(false);
      setInviteForm({ email: '', first_name: '', last_name: '', company_name: '' });
      refetchContacts();
      refetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const handleResend = async (contactId) => {
    try {
      await resend(contactId);
      toast.success('Invitation renvoyée !');
      refetchContacts();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erreur lors du renvoi';
      toast.error(msg);
    }
  };

  const handleDelete = async (contactId) => {
    if (window.confirm('Supprimer ce contact ?')) {
      try {
        await deleteContact(contactId);
        toast.success('Contact supprimé');
        refetchContacts();
        refetchStats();
      } catch (err) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const tier = stats?.summary?.tier || 'bronze';
  const tierStyle = TIER_STYLES[tier];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Share2 className="w-5 h-5 text-brand-green" />
          Programme de Parrainage
        </h2>
        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-brand-green text-black font-medium rounded-lg hover:bg-brand-green/90 transition-all"
        >
          <UserPlus size={16} />
          Inviter un contact
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-800 pb-2">
        {[
          { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
          { id: 'contacts', label: 'Contacts', icon: Users },
          { id: 'leaderboard', label: 'Classement', icon: Trophy }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
              activeSection === tab.id
                ? 'bg-brand-green/20 text-brand-green'
                : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Dashboard Section */}
      {activeSection === 'dashboard' && (
        <div className="space-y-6">
          {/* Referral Link Card */}
          <div className="bg-gradient-to-r from-brand-green/20 to-cyan-500/10 rounded-xl p-6 border border-brand-green/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-brand-green/20 flex items-center justify-center">
                <Link2 className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Votre lien de parrainage</h3>
                <p className="text-gray-400 text-sm">Partagez ce lien pour inviter de nouveaux utilisateurs</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-[#0d0d0d] rounded-lg p-3 border border-gray-800">
                <code className="text-brand-green text-sm break-all">{linkData?.referral_link || 'Chargement...'}</code>
              </div>
              <button
                onClick={copyReferralLink}
                className="px-4 py-3 bg-brand-green text-black font-medium rounded-lg hover:bg-brand-green/90 transition-all flex items-center gap-2"
              >
                <Copy size={16} />
                Copier
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          {statsLoading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="animate-spin text-brand-green" size={32} />
            </div>
          ) : (
            <>
              {/* Tier & Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Tier Card */}
                <div className={`rounded-xl p-6 border ${tierStyle.bg} ${tierStyle.border}`}>
                  <div className="flex items-center gap-4">
                    <span className="text-4xl flex-shrink-0">{tierStyle.icon}</span>
                    <div className="min-w-0">
                      <h3 className="text-gray-400 text-sm">Votre niveau</h3>
                      <div className={`text-2xl font-bold ${tierStyle.text} capitalize`}>{tier}</div>
                    </div>
                  </div>
                  {stats?.summary?.next_tier && (
                    <div className="mt-4">
                      <div className="flex justify-between text-sm mb-1 gap-2">
                        <span className="text-gray-400 truncate">Prochain niveau: {stats.summary.next_tier}</span>
                        <span className="text-gray-300 whitespace-nowrap">{stats.summary.value_to_next_tier?.toFixed(0)}€ restants</span>
                      </div>
                      <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${tierStyle.bg.replace('/20', '')} transition-all`}
                          style={{
                            width: `${Math.min(100, (stats.summary.total_value_generated / (stats.summary.total_value_generated + (stats.summary.value_to_next_tier || 0))) * 100)}%`
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Credits Earned */}
                <div className="bg-[#0d0d0d] rounded-xl p-6 border border-gray-800">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-brand-green/20 flex items-center justify-center flex-shrink-0">
                      <Gift className="w-7 h-7 text-brand-green" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-gray-400 text-sm">Crédits gagnés</h3>
                      <div className="text-3xl font-bold text-white">{stats?.summary?.total_credits_earned || 0}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Funnel Stats */}
              <div className="bg-[#0d0d0d] rounded-xl p-6 border border-gray-800">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-brand-green" />
                  Entonnoir de conversion
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Invités', value: stats?.funnel?.invited || 0, color: 'text-blue-400' },
                    { label: 'Ont cliqué', value: stats?.funnel?.clicked || 0, color: 'text-yellow-400' },
                    { label: 'Inscrits', value: stats?.funnel?.signed_up || 0, color: 'text-brand-green' },
                    { label: 'Convertis', value: stats?.funnel?.converted || 0, color: 'text-purple-400' }
                  ].map((stat, i) => (
                    <div key={i} className="text-center">
                      <div className={`text-xl md:text-2xl font-bold ${stat.color}`}>{stat.value}</div>
                      <div className="text-gray-500 text-xs md:text-sm">{stat.label}</div>
                    </div>
                  ))}
                </div>
                {stats?.funnel && (
                  <div className="mt-4 flex justify-center gap-6 text-sm">
                    <span className="text-gray-400">
                      Taux de clic: <span className="text-white font-medium">{stats.funnel.click_rate}%</span>
                    </span>
                    <span className="text-gray-400">
                      Taux de conversion: <span className="text-white font-medium">{stats.funnel.conversion_rate}%</span>
                    </span>
                  </div>
                )}
              </div>

              {/* Rewards Info */}
              <div className="bg-[#0d0d0d] rounded-xl p-6 border border-gray-800">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-brand-green" />
                  Récompenses
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-[#111111] rounded-lg p-4 border border-gray-800">
                    <div className="text-yellow-400 font-bold text-base md:text-lg">+1 crédit</div>
                    <div className="text-gray-400 text-sm">Pour chaque clic</div>
                  </div>
                  <div className="bg-[#111111] rounded-lg p-4 border border-gray-800">
                    <div className="text-brand-green font-bold text-base md:text-lg">+3 scans</div>
                    <div className="text-gray-400 text-sm">Pour chaque inscription</div>
                  </div>
                  <div className="bg-[#111111] rounded-lg p-4 border border-gray-800">
                    <div className="text-purple-400 font-bold text-base md:text-lg">+10 scans, +2 rapports</div>
                    <div className="text-gray-400 text-sm">Pour chaque conversion Pro</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Contacts Section */}
      {activeSection === 'contacts' && (
        <div className="space-y-4">
          {contactsLoading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="animate-spin text-brand-green" size={32} />
            </div>
          ) : contacts?.length === 0 ? (
            <div className="bg-[#0d0d0d] rounded-xl p-8 border border-gray-800 text-center">
              <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Aucun contact invite pour le moment</p>
              <button
                onClick={() => setShowInviteModal(true)}
                className="mt-4 px-4 py-2 bg-brand-green text-black font-medium rounded-lg hover:bg-brand-green/90 transition-all"
              >
                Inviter votre premier contact
              </button>
            </div>
          ) : (
            <div className="bg-[#0d0d0d] rounded-xl border border-gray-800 overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#111111]">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Contact</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Statut</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Credits générés</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Date</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((contact) => {
                    const statusStyle = STATUS_STYLES[contact.status] || STATUS_STYLES.invited;
                    return (
                      <tr key={contact.id} className="border-t border-gray-800 hover:bg-gray-800/30">
                        <td className="px-4 py-3">
                          <div>
                            <div className="text-white font-medium">
                              {contact.first_name || contact.email.split('@')[0]}
                              {contact.last_name && ` ${contact.last_name}`}
                            </div>
                            <div className="text-gray-500 text-sm">{contact.email}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-lg ${statusStyle.bg} ${statusStyle.text}`}>
                            {statusStyle.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-brand-green font-medium">
                          +{contact.total_credits_earned || 0}
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-sm">
                          {contact.created_at ? new Date(contact.created_at).toLocaleDateString('fr-FR') : '-'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            {contact.status === 'invited' && (
                              <button
                                onClick={() => handleResend(contact.id)}
                                className="p-2 text-gray-400 hover:text-brand-green hover:bg-gray-800 rounded-lg transition-all"
                                title="Renvoyer l'invitation"
                              >
                                <Send size={16} />
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(contact.id)}
                              className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-800 rounded-lg transition-all"
                              title="Supprimer"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Leaderboard Section */}
      {activeSection === 'leaderboard' && (
        <div className="space-y-4">
          {leaderboardLoading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="animate-spin text-brand-green" size={32} />
            </div>
          ) : (
            <div className="bg-[#0d0d0d] rounded-xl border border-gray-800 overflow-hidden">
              <div className="p-4 border-b border-gray-800 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" />
                <h3 className="text-white font-semibold">Top Parrains</h3>
              </div>
              <table className="w-full">
                <thead className="bg-[#111111]">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Rang</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Utilisateur</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Tier</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Filleuls</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Conversions</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Valeur</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard?.map((user) => {
                    const userTierStyle = TIER_STYLES[user.tier] || TIER_STYLES.bronze;
                    return (
                      <tr
                        key={user.user_id}
                        className={`border-t border-gray-800 ${user.is_current_user ? 'bg-brand-green/5' : 'hover:bg-gray-800/30'}`}
                      >
                        <td className="px-4 py-3">
                          <span className={`font-bold ${user.rank <= 3 ? 'text-yellow-500' : 'text-gray-400'}`}>
                            #{user.rank}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`text-white ${user.is_current_user ? 'font-bold' : ''}`}>
                              {user.username}
                            </span>
                            {user.is_current_user && (
                              <span className="text-xs text-brand-green">(vous)</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm ${userTierStyle.text} capitalize`}>
                            {userTierStyle.icon} {user.tier}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-white">{user.total_referrals}</td>
                        <td className="px-4 py-3 text-right text-brand-green font-medium">{user.conversions}</td>
                        <td className="px-4 py-3 text-right text-white font-medium">{user.total_value?.toFixed(0)}EUR</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#0d0d0d] border border-gray-800 rounded-2xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-brand-green" />
                Inviter un contact
              </h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="text-gray-400 hover:text-white"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">E-mail *</label>
                <input
                  type="email"
                  required
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="w-full px-4 py-3 bg-[#111111] border border-gray-800 rounded-xl text-white focus:border-brand-green focus:ring-1 focus:ring-brand-green outline-none"
                  placeholder="membre@exemple.fr"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Prénom</label>
                  <input
                    type="text"
                    value={inviteForm.first_name}
                    onChange={(e) => setInviteForm({ ...inviteForm, first_name: e.target.value })}
                    className="w-full px-4 py-3 bg-[#111111] border border-gray-800 rounded-xl text-white focus:border-brand-green focus:ring-1 focus:ring-brand-green outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Nom</label>
                  <input
                    type="text"
                    value={inviteForm.last_name}
                    onChange={(e) => setInviteForm({ ...inviteForm, last_name: e.target.value })}
                    className="w-full px-4 py-3 bg-[#111111] border border-gray-800 rounded-xl text-white focus:border-brand-green focus:ring-1 focus:ring-brand-green outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Entreprise</label>
                <input
                  type="text"
                  value={inviteForm.company_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, company_name: e.target.value })}
                  className="w-full px-4 py-3 bg-[#111111] border border-gray-800 rounded-xl text-white focus:border-brand-green focus:ring-1 focus:ring-brand-green outline-none"
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={inviteLoading}
                  className="flex items-center gap-2 px-6 py-2 bg-brand-green text-black font-medium rounded-lg hover:bg-brand-green/90 transition-all disabled:opacity-50"
                >
                  {inviteLoading ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : (
                    <Send size={16} />
                  )}
                  Envoyer l'invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// END REFERRAL TAB COMPONENT
// ============================================

const Account = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'profile';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // i18n hook for translations
  const { language, setLanguage, t } = useI18n();

  // Profile state
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    job_title: '',
    avatar_url: '',
    language: 'fr'
  });

  // Company state
  const [company, setCompany] = useState({
    name: '',
    siren: '',
    siret: '',
    legal_form: '',
    capital: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    country_code: 'FR',
    timezone: 'Europe/Paris',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    logo_url: '',
    logo_hash: null,
    logo_antivirus_scanned: false,
    logo_antivirus_clean: null
  });

  // Security state
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaSetup, setMfaSetup] = useState(null);
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState(false);
  const [apiKeyInfo, setApiKeyInfo] = useState(null);

  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [workspacesCount, setWorkspacesCount] = useState(0);
  const [invoices, setInvoices] = useState([]);

  // Team members state
  const [teamMembers, setTeamMembers] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', first_name: '', last_name: '', role: 'analyst' });

  // Credits state
  const [creditSummary, setCreditSummary] = useState(null);
  const [showCreditPackModal, setShowCreditPackModal] = useState(false);
  const [purchasingPack, setPurchasingPack] = useState(null);

  // Activity history state
  const [activityHistory, setActivityHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Integrations state
  const [activeWizard, setActiveWizard] = useState(null);
  const [manualConfigModal, setManualConfigModal] = useState(null);
  const [integrations, setIntegrations] = useState({});

  // Targets count (distinct targets scanned)
  const [targetsUsed, setTargetsUsed] = useState(0);

  // Security tab states
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [sendingSMS, setSendingSMS] = useState(false);

  // Team collaboration states
  const [securityTeamMembers, setSecurityTeamMembers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('analyst');
  const [inviting, setInviting] = useState(false);

  // API docs state
  const [showApiDocs, setShowApiDocs] = useState(false);
  const [copiedEndpoint, setCopiedEndpoint] = useState(null);

  // Mobile app state
  const [mobileAppImageZoomed, setMobileAppImageZoomed] = useState(false);

  // Check if user has a paid plan (can manage team members)
  const isPaidPlan = subscription?.plan && ['pro', 'pro_monthly', 'pro_audit', 'entreprise', 'enterprise', 'enterprise_plus', 'groupe', 'dedicated'].includes(subscription.plan);
  // Check if user is super_admin (always has access to team management)
  const isSuperAdmin = profile?.role === 'super_admin';
  // Show team tab for paid plans OR super_admins
  const isEnterprisePlan = isPaidPlan || isSuperAdmin;

  // Grouped tabs by category
  const tabGroups = [
    {
      title: 'Mon Compte',
      tabs: [
        { id: 'profile', label: 'Profil', icon: User },
        { id: 'security', label: 'Sécurité', icon: Shield },
      ]
    },
    {
      title: 'Facturation',
      tabs: [
        { id: 'subscription', label: 'Abonnement', icon: CreditCard },
      ]
    },
    {
      title: 'Configuration',
      tabs: [
        { id: 'contract', label: 'Contrat', icon: FileSignature },
        { id: 'reports', label: 'Type de rapports', icon: ClipboardList },
        { id: 'integrations', label: 'Intégrations', icon: Plug },
        { id: 'notifications', label: 'Notifications', icon: Bell },
      ]
    },
    {
      title: 'Équipe',
      tabs: [
        ...(isEnterprisePlan ? [{ id: 'team', label: 'Utilisateurs', icon: Users }] : []),
      ]
    },
    {
      title: 'Aide',
      tabs: [
        { id: 'history', label: 'Historique', icon: History },
        { id: 'version', label: 'Version', icon: Info },
        { id: 'support', label: 'Support', icon: HelpCircle },
      ]
    }
  ];

  // Flatten tabs for URL validation
  const tabs = tabGroups.flatMap(group => group.tabs);

  useEffect(() => {
    loadAccountData();
  }, []);

  useEffect(() => {
    // Update tab when URL changes
    const tab = searchParams.get('tab');
    if (tab && tabs.some(t => t.id === tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const loadAccountData = async () => {
    try {
      setLoading(true);

      // Load profile
      const profileData = await account.getProfile();
      setProfile({
        ...profileData,
        language: profileData.language || 'fr'
      });
      setMfaEnabled(profileData.mfa_enabled || false);

      // Load company info if available
      if (profileData.company) {
        setCompany({
          name: profileData.company.name || '',
          siren: profileData.company.siren || '',
          siret: profileData.company.siret || '',
          legal_form: profileData.company.legal_form || '',
          capital: profileData.company.capital || '',
          address: profileData.company.address || '',
          city: profileData.company.city || '',
          postal_code: profileData.company.postal_code || '',
          country: profileData.company.country || 'France',
          country_code: profileData.company.country_code || 'FR',
          timezone: profileData.company.timezone || 'Europe/Paris',
          contact_name: profileData.company.contact_name || '',
          contact_email: profileData.company.contact_email || '',
          contact_phone: profileData.company.contact_phone || '',
          logo_url: profileData.company.logo_url || ''
        });
      }

      // Load subscription
      try {
        const subData = await billing.getSubscription();
        setSubscription(subData);
      } catch (e) {
        console.log('No subscription');
      }

      // Load usage
      try {
        const usageData = await billing.getUsage();
        setUsage(usageData);
      } catch (e) {
        console.log('No usage data');
      }

      // Load workspaces count
      try {
        const wsCount = await workspacesApi.count();
        setWorkspacesCount(wsCount.count || 0);
      } catch (e) {
        console.log('No workspaces');
      }

      // Load invoices
      try {
        const invoicesData = await billing.getInvoices(10);
        setInvoices(invoicesData);
      } catch (e) {
        console.log('No invoices');
      }

      // Load API key info
      try {
        const keyInfo = await account.getApiKey();
        setApiKeyInfo(keyInfo);
      } catch (e) {
        // API key not configuréd
      }

      // Load team members if enterprise plan
      try {
        const teamData = await account.getTeam();
        setTeamMembers(teamData);
      } catch (e) {
        console.log('No team data or not authorized');
      }

      // Load credit summary
      try {
        const creditData = await creditsApi.getSummary();
        setCreditSummary(creditData);
      } catch (e) {
        console.log('No credit data');
      }

      // Load targets used count from usage
      try {
        const usageData = await billing.getUsage();
        if (usageData) {
          setUsage(usageData);
          setTargetsUsed(usageData.targets_used || 0);
        }
      } catch (e) {
        console.log('No targets data');
      }

    } catch (error) {
      console.error('Error loading account:', error);
      toast.error('Erreur lors du chargement du compte');
    } finally {
      setLoading(false);
    }
  };

  // Load activity history when history tab is active
  const loadActivityHistory = async () => {
    if (historyLoading) return;
    setHistoryLoading(true);
    try {
      const historyData = await historyActions.getActions({ limit: 100 });
      // API returns { actions: [...], total, page, limit, pages }
      setActivityHistory(historyData.actions || historyData.items || []);
    } catch (e) {
      console.log('No activity history:', e);
      setActivityHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Load history when tab changes to history
  useEffect(() => {
    if (activeTab === 'history' && activityHistory.length === 0) {
      loadActivityHistory();
    }
  }, [activeTab]);

  const handleInviteTeamMember = async () => {
    if (!inviteData.email) {
      toast.error('E-mail requis');
      return;
    }

    try {
      setSaving(true);
      await account.inviteTeamMember(inviteData);
      toast.success('Invitation envoyée');
      setShowInviteModal(false);
      setInviteData({ email: '', first_name: '', last_name: '', role: 'analyst' });
      // Reload team
      const teamData = await account.getTeam();
      setTeamMembers(teamData);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'invitation');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveTeamMember = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir retirer ce membre ?')) return;

    try {
      await account.removeTeamMember(userId);
      toast.success('Membre retiré');
      setTeamMembers(teamMembers.filter(m => m.id !== userId));
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleResendInvitation = async (userId) => {
    try {
      await account.resendInvitation(userId);
      toast.success('Invitation renvoyée');
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Erreur lors du renvoi';
      toast.error(msg);
    }
  };

  // Credit packs for purchase modal
  const creditPacks = [
    {
      slug: 'pack_10',
      name: 'Starter',
      credits: 10,
      price: 10,
      bonus: 0,
      popular: false,
      color: 'gray'
    },
    {
      slug: 'pack_50',
      name: 'Standard',
      credits: 50,
      price: 45,
      bonus: 5,
      popular: true,
      color: 'blue'
    },
    {
      slug: 'pack_100',
      name: 'Pro',
      credits: 100,
      price: 80,
      bonus: 20,
      popular: false,
      color: 'green'
    },
    {
      slug: 'pack_500',
      name: 'Enterprise',
      credits: 500,
      price: 350,
      bonus: 150,
      popular: false,
      color: 'purple'
    }
  ];

  const handlePurchasePack = async (pack) => {
    try {
      setPurchasingPack(pack.slug);
      const result = await creditsApi.purchasePack(pack.slug);
      toast.success(`${result.credits_added} crédits ajoutés à votre compte !`);
      setShowCreditPackModal(false);
      // Refresh credit summary
      const creditData = await creditsApi.getSummary();
      setCreditSummary(creditData);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'achat');
    } finally {
      setPurchasingPack(null);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez selectionner une image');
      return;
    }

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 2 Mo');
      return;
    }

    setSaving(true);

    // Convert to base64 for preview and upload
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result;
      // Show preview immediately
      setProfile(prev => ({ ...prev, avatar_url: base64 }));

      try {
        const result = await account.updateAvatar(base64);
        // Update with server URL
        if (result.avatar_url) {
          setProfile(prev => ({ ...prev, avatar_url: result.avatar_url }));
        }
        toast.success('Avatar mis a jour');
      } catch (error) {
        toast.error('Erreur lors de la mise a jour de l\'avatar');
        // Revert preview on error
        setProfile(prev => ({ ...prev, avatar_url: '' }));
      } finally {
        setSaving(false);
      }
    };

    reader.onerror = () => {
      toast.error('Erreur de lecture du fichier');
      setSaving(false);
    };

    reader.readAsDataURL(file);
  };

  const handleProfileSave = async () => {
    try {
      setSaving(true);
      await account.updateProfile({
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone,
        job_title: profile.job_title,
        language: profile.language
      });
      toast.success('Profil mis a jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleCompanySave = async () => {
    try {
      setSaving(true);
      await account.updateCompany(company);
      toast.success('Informations société mises à jour avec succès !');

      // Reload profile to get updated company data
      const profileData = await account.getProfile();
      if (profileData.company) {
        setCompany({
          name: profileData.company.name || '',
          siren: profileData.company.siren || '',
          siret: profileData.company.siret || '',
          legal_form: profileData.company.legal_form || '',
          capital: profileData.company.capital || '',
          address: profileData.company.address || '',
          city: profileData.company.city || '',
          postal_code: profileData.company.postal_code || '',
          country: profileData.company.country || 'France',
          country_code: profileData.company.country_code || 'FR',
          timezone: profileData.company.timezone || 'Europe/Paris',
          contact_name: profileData.company.contact_name || '',
          contact_email: profileData.company.contact_email || '',
          contact_phone: profileData.company.contact_phone || '',
          logo_url: profileData.company.logo_url || ''
        });
      }
    } catch (error) {
      console.error('Company save error:', error);
      toast.error('Erreur lors de la sauvegarde: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez selectionner une image');
      return;
    }

    // Check file size (max 2MB - aligned with backend limit)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 2 Mo');
      return;
    }

    setSaving(true);

    // Convert to base64 for preview and upload
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result;
      setCompany(prev => ({ ...prev, logo_url: base64 }));

      try {
        const result = await account.updateCompanyLogo(base64);
        setCompany(prev => ({
          ...prev,
          logo_url: result.logo_url,
          logo_hash: result.file_hash,
          logo_antivirus_scanned: result.antivirus_scanned,
          logo_antivirus_clean: result.antivirus_clean
        }));
        if (result.antivirus_scanned && result.antivirus_clean) {
          toast.success('Logo mis a jour - Scan antivirus OK');
        } else {
          toast.success('Logo mis a jour');
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de la mise a jour du logo');
        console.error('Logo upload error:', error);
        // Revert preview on error
        setCompany(prev => ({ ...prev, logo_url: '' }));
      } finally {
        setSaving(false);
      }
    };

    reader.onerror = () => {
      toast.error('Erreur de lecture du fichier');
      setSaving(false);
    };

    reader.readAsDataURL(file);
  };

  const handleLogoDelete = async () => {
    try {
      setSaving(true);
      await account.deleteCompanyLogo();
      setCompany({ ...company, logo_url: '', logo_hash: null, logo_antivirus_scanned: false, logo_antivirus_clean: null });
      toast.success('Logo supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression du logo');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwords.new !== passwords.confirm) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwords.new.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    try {
      setSaving(true);
      await account.changePassword(passwords.current, passwords.new);
      toast.success('Mot de passe modifié');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleMfaSetup = async () => {
    try {
      const setup = await account.setupMfa();
      setMfaSetup(setup);
      setShowMfaModal(true);
    } catch (error) {
      toast.error('Erreur lors de la configuration MFA');
    }
  };

  const handleMfaEnable = async () => {
    try {
      await account.enableMfa(mfaCode);
      setMfaEnabled(true);
      setShowMfaModal(false);
      setMfaCode('');
      toast.success('MFA active');
    } catch (error) {
      toast.error('Code invalide');
    }
  };

  const handleMfaDisable = async () => {
    const code = prompt('Entrez votre code MFA pour desactiver');
    if (!code) return;

    try {
      await account.disableMfa(code);
      setMfaEnabled(false);
      toast.success('MFA desactive');
    } catch (error) {
      toast.error('Code invalide');
    }
  };

  const handleGenerateApiKey = async () => {
    if (!window.confirm('Générer une nouvelle clé API ? L\'ancienne sera invalidee.')) return;

    try {
      const result = await account.generateApiKey();
      setApiKeyInfo({ has_key: true, masked_key: result.api_key });
      navigator.clipboard.writeText(result.api_key);
      toast.success('Clé API générée et copiée');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getInitials = () => {
    if (profile.first_name && profile.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase();
    }
    return profile.email?.[0]?.toUpperCase() || 'U';
  };

  const formatQuota = (value, used = 0) => {
    if (value === -1) return { text: 'Illimite', percent: 0 };
    const percent = value > 0 ? Math.min((used / value) * 100, 100) : 0;
    return { text: `${used} / ${value}`, percent };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
      </div>
    );
  }

  const planStyle = PLAN_STYLES[subscription?.plan] || PLAN_STYLES.free;
  const PlanIcon = planStyle.icon;

  return (
    <main className="min-h-[calc(100vh-200px)] flex flex-col">
      <div className="container mx-auto px-6 lg:pl-[68px] py-8 flex-1">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Mon Compte</h1>
          <p className="text-gray-400">Gérez votre profil, sécurité et abonnement</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="liquid-glass rounded-xl p-4 border border-brand-green/20 sticky top-24">
              {/* User Card */}
              <div className="text-center mb-6 pb-6 border-b border-gray-800">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-brand-green/30 to-brand-green/10 flex items-center justify-center text-brand-green font-bold text-2xl border-2 border-brand-green/30">
                  {profile.avatar_url ? (
                    <img src={profile.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    getInitials()
                  )}
                </div>
                <h3 className="text-white font-semibold">
                  {profile.first_name} {profile.last_name}
                </h3>
                <p className="text-gray-400 text-sm truncate">{profile.email}</p>

                {/* Plan Badge */}
                <div className={`inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-full ${planStyle.bg} ${planStyle.border} border`}>
                  <PlanIcon className={`w-4 h-4 ${planStyle.color}`} />
                  <span className={`text-sm font-medium ${planStyle.color}`}>
                    {subscription?.plan_name || 'Gratuit'}
                  </span>
                </div>
              </div>

              {/* Navigation */}
              <nav className="space-y-4">
                {tabGroups.map((group, groupIndex) => (
                  group.tabs.length > 0 && (
                    <div key={group.title}>
                      {/* Group Header */}
                      <h4 className={`text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 mb-2 ${groupIndex > 0 ? 'pt-3 border-t border-gray-800' : ''}`}>
                        {group.title}
                      </h4>
                      {/* Group Tabs */}
                      <div className="space-y-1">
                        {group.tabs.map((tab) => {
                          const Icon = tab.icon;
                          return (
                            <button
                              key={tab.id}
                              onClick={() => setActiveTab(tab.id)}
                              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all ${
                                activeTab === tab.id
                                  ? 'bg-brand-green/10 text-brand-green border border-brand-green/30'
                                  : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                              }`}
                            >
                              <Icon className="w-4 h-4" />
                              <span className="font-medium text-sm">{tab.label}</span>
                              {activeTab === tab.id && (
                                <ChevronRight className="w-4 h-4 ml-auto" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="liquid-glass rounded-xl border border-brand-green/20">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="p-6">
                  {/* Avatar Section */}
                  <div className="flex items-center gap-6 mb-8 pb-8 border-b border-gray-800">
                    <div className="relative flex-shrink-0">
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-brand-green/30 to-brand-green/10 flex items-center justify-center text-brand-green font-bold text-3xl border-2 border-brand-green/30 overflow-hidden">
                        {profile.avatar_url ? (
                          <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          getInitials()
                        )}
                      </div>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="absolute bottom-0 right-0 w-8 h-8 bg-brand-green rounded-full flex items-center justify-center text-black hover:bg-brand-green/90 transition-colors shadow-lg"
                      >
                        <Camera className="w-4 h-4" />
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        className="hidden"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-white font-semibold text-lg">Photo de profil</h3>
                      <p className="text-gray-400 text-sm">JPG, PNG ou GIF. Max 2 Mo.</p>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="mt-2 text-brand-green hover:text-brand-green/80 text-sm font-medium whitespace-nowrap"
                      >
                        Changer la photo
                      </button>
                    </div>
                  </div>

                  <h2 className="text-xl font-semibold text-white mb-6">Informations personnelles</h2>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Prénom</label>
                      <input
                        type="text"
                        value={profile.first_name || ''}
                        onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                        className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                 focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Nom</label>
                      <input
                        type="text"
                        value={profile.last_name || ''}
                        onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                        className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                 focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">E-mail</label>
                      <input
                        type="email"
                        value={profile.email || ''}
                        disabled
                        className="w-full bg-[#0a0a0a] border border-gray-800 rounded-xl px-4 py-3 text-gray-500 cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Téléphone</label>
                      <input
                        type="tel"
                        value={profile.phone || ''}
                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                        className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                 focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                        placeholder="+33 6 12 34 56 78"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-300 mb-2">Poste</label>
                      <input
                        type="text"
                        value={profile.job_title || ''}
                        onChange={(e) => setProfile({ ...profile, job_title: e.target.value })}
                        className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                 focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                        placeholder="Ex: RSSI, DSI, Pentester..."
                      />
                    </div>
                  </div>

                  {/* Company Section */}
                  <div className="mt-8 pt-8 border-t border-gray-800">
                    <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-brand-green" />
                      Informations société
                    </h2>

                    {/* Company Logo Section */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-300 mb-3">Logo de la société</label>
                      <div className="flex items-center gap-4">
                        {/* Logo Preview */}
                        <div className="relative w-24 h-24 rounded-xl overflow-hidden bg-[#111111] border border-gray-800 flex items-center justify-center flex-shrink-0">
                          {company.logo_url ? (
                            <img
                              src={company.logo_url}
                              alt="Logo de la société"
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <Image className="w-10 h-10 text-gray-600" />
                          )}
                          {/* Antivirus Badge */}
                          {company.logo_url && company.logo_antivirus_scanned && company.logo_antivirus_clean && (
                            <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1 shadow-lg" title={`Scan antivirus OK\nHash: ${company.logo_hash}`}>
                              <Shield className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                        {/* Upload/Delete Actions */}
                        <div className="flex flex-col gap-2 min-w-0 flex-1 overflow-hidden">
                          <input
                            type="file"
                            id="company-logo-input"
                            accept="image/png,image/jpeg,image/gif,image/webp"
                            onChange={handleLogoChange}
                            className="hidden"
                          />
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => document.getElementById('company-logo-input').click()}
                              disabled={saving}
                              className="flex items-center gap-2 px-4 py-2 bg-brand-green/20 text-brand-green rounded-lg
                                       hover:bg-brand-green/30 transition-colors text-sm font-medium"
                            >
                              <Upload className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate">{company.logo_url ? 'Changer le logo' : 'Ajouter un logo'}</span>
                            </button>
                            {company.logo_url && (
                              <button
                                type="button"
                                onClick={handleLogoDelete}
                                disabled={saving}
                                className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg
                                         hover:bg-red-500/30 transition-colors text-sm font-medium"
                              >
                                <Trash2 className="w-4 h-4 flex-shrink-0" />
                                <span>Supprimer</span>
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 break-words">PNG, JPG, GIF ou WEBP. Max 2 Mo.</p>
                          {/* Antivirus Status */}
                          {company.logo_url && company.logo_hash && (
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              {company.logo_antivirus_scanned && company.logo_antivirus_clean ? (
                                <div className="flex items-center gap-1 text-xs text-green-400">
                                  <Shield className="w-3 h-3 flex-shrink-0" />
                                  <span>Scan antivirus OK</span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 text-xs text-gray-500">
                                  <Shield className="w-3 h-3 flex-shrink-0" />
                                  <span>Non scanné</span>
                                </div>
                              )}
                              <span className="text-xs text-gray-600">|</span>
                              <span className="text-xs text-gray-500 font-mono truncate" title="Hash MD5 du fichier">
                                {company.logo_hash?.substring(0, 8)}...
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Nom de la société</label>
                        <input
                          type="text"
                          value={company.name || ''}
                          onChange={(e) => setCompany({ ...company, name: e.target.value })}
                          className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                   focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Forme juridique</label>
                        <input
                          type="text"
                          value={company.legal_form || ''}
                          onChange={(e) => setCompany({ ...company, legal_form: e.target.value })}
                          className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                   placeholder:text-gray-500 placeholder:italic
                                   focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                          placeholder="SAS, SARL, EURL..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">SIREN</label>
                        <input
                          type="text"
                          value={company.siren || ''}
                          onChange={(e) => setCompany({ ...company, siren: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                          className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                   placeholder:text-gray-500 placeholder:italic
                                   focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                          placeholder="N° SIREN (9 chiffres)"
                          maxLength={9}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">SIRET</label>
                        <input
                          type="text"
                          value={company.siret || ''}
                          onChange={(e) => setCompany({ ...company, siret: e.target.value.replace(/\D/g, '').slice(0, 14) })}
                          className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                   placeholder:text-gray-500 placeholder:italic
                                   focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                          placeholder="N° SIRET (14 chiffres)"
                          maxLength={14}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Capital social</label>
                        <input
                          type="text"
                          value={company.capital || ''}
                          onChange={(e) => setCompany({ ...company, capital: e.target.value })}
                          className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                   focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                          placeholder="10 000 €"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-300 mb-2">Adresse</label>
                        <input
                          type="text"
                          value={company.address || ''}
                          onChange={(e) => setCompany({ ...company, address: e.target.value })}
                          className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                   focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Ville</label>
                        <input
                          type="text"
                          value={company.city || ''}
                          onChange={(e) => setCompany({ ...company, city: e.target.value })}
                          className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                   focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Code postal</label>
                        <input
                          type="text"
                          value={company.postal_code || ''}
                          onChange={(e) => setCompany({ ...company, postal_code: e.target.value })}
                          className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                   focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Pays</label>
                        <select
                          value={company.country_code || 'FR'}
                          onChange={(e) => {
                            const code = e.target.value;
                            const countryMap = {
                              'FR': 'France', 'BE': 'Belgique', 'CH': 'Suisse', 'LU': 'Luxembourg',
                              'CA': 'Canada', 'DE': 'Allemagne', 'ES': 'Espagne', 'IT': 'Italie',
                              'GB': 'Royaume-Uni', 'US': 'Etats-Unis', 'PT': 'Portugal', 'NL': 'Pays-Bas',
                              'AT': 'Autriche', 'PL': 'Pologne', 'MA': 'Maroc', 'TN': 'Tunisie',
                              'SN': 'Senegal', 'CI': "Cote d'Ivoire"
                            };
                            setCompany({ ...company, country_code: code, country: countryMap[code] || code });
                          }}
                          className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                   focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                        >
                          <option value="FR">France</option>
                          <option value="BE">Belgique</option>
                          <option value="CH">Suisse</option>
                          <option value="LU">Luxembourg</option>
                          <option value="CA">Canada</option>
                          <option value="DE">Allemagne</option>
                          <option value="ES">Espagne</option>
                          <option value="IT">Italie</option>
                          <option value="GB">Royaume-Uni</option>
                          <option value="US">Etats-Unis</option>
                          <option value="PT">Portugal</option>
                          <option value="NL">Pays-Bas</option>
                          <option value="AT">Autriche</option>
                          <option value="PL">Pologne</option>
                          <option value="MA">Maroc</option>
                          <option value="TN">Tunisie</option>
                          <option value="SN">Senegal</option>
                          <option value="CI">Cote d'Ivoire</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Fuseau horaire (pour les scans)</label>
                        <select
                          value={company.timezone || 'Europe/Paris'}
                          onChange={(e) => setCompany({ ...company, timezone: e.target.value })}
                          className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                   focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                        >
                          <optgroup label="Europe">
                            <option value="Europe/Paris">Paris (UTC+1, été UTC+2)</option>
                            <option value="Europe/Brussels">Bruxelles (UTC+1, été UTC+2)</option>
                            <option value="Europe/Zurich">Zurich (UTC+1, été UTC+2)</option>
                            <option value="Europe/Luxembourg">Luxembourg (UTC+1, été UTC+2)</option>
                            <option value="Europe/Berlin">Berlin (UTC+1, été UTC+2)</option>
                            <option value="Europe/Madrid">Madrid (UTC+1, été UTC+2)</option>
                            <option value="Europe/Rome">Rome (UTC+1, été UTC+2)</option>
                            <option value="Europe/London">Londres (UTC+0, été UTC+1)</option>
                            <option value="Europe/Amsterdam">Amsterdam (UTC+1, été UTC+2)</option>
                            <option value="Europe/Vienna">Vienne (UTC+1, été UTC+2)</option>
                            <option value="Europe/Warsaw">Varsovie (UTC+1, été UTC+2)</option>
                            <option value="Europe/Lisbon">Lisbonne (UTC+0, été UTC+1)</option>
                          </optgroup>
                          <optgroup label="Amérique">
                            <option value="America/Montreal">Montréal (UTC-5, été UTC-4)</option>
                            <option value="America/Toronto">Toronto (UTC-5, été UTC-4)</option>
                            <option value="America/New_York">New York (UTC-5, été UTC-4)</option>
                            <option value="America/Los_Angeles">Los Angeles (UTC-8, été UTC-7)</option>
                            <option value="America/Chicago">Chicago (UTC-6, été UTC-5)</option>
                          </optgroup>
                          <optgroup label="Afrique">
                            <option value="Africa/Casablanca">Casablanca (UTC+0, été UTC+1)</option>
                            <option value="Africa/Tunis">Tunis (UTC+1)</option>
                            <option value="Africa/Dakar">Dakar (UTC+0)</option>
                            <option value="Africa/Abidjan">Abidjan (UTC+0)</option>
                          </optgroup>
                          <optgroup label="Autres">
                            <option value="UTC">UTC (Temps universel)</option>
                            <option value="Asia/Dubai">Dubaï (UTC+4)</option>
                            <option value="Asia/Singapore">Singapour (UTC+8)</option>
                            <option value="Asia/Tokyo">Tokyo (UTC+9)</option>
                          </optgroup>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Les dates et heures des scans seront affichées dans ce fuseau horaire</p>
                      </div>

                      {/* Représentant légal */}
                      <div className="md:col-span-2 mt-6 pt-6 border-t border-gray-800">
                        <h3 className="text-lg font-medium text-white mb-4">Représentant légal</h3>
                        <div className="grid md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Nom complet</label>
                            <input
                              type="text"
                              value={company.contact_name || ''}
                              onChange={(e) => setCompany({ ...company, contact_name: e.target.value })}
                              className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                       focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                              placeholder="Jean Dupont"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">E-mail</label>
                            <input
                              type="email"
                              value={company.contact_email || ''}
                              onChange={(e) => setCompany({ ...company, contact_email: e.target.value })}
                              className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                       focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                              placeholder="nom@entreprise.com"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Téléphone</label>
                            <input
                              type="tel"
                              value={company.contact_phone || ''}
                              onChange={(e) => setCompany({ ...company, contact_phone: e.target.value })}
                              className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                       focus:outline-none focus:border-brand-green/50 focus:ring-2 focus:ring-brand-green/20"
                              placeholder="+33 6 12 34 56 78"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Team Members Section */}
                  <div className="mt-8 pt-8 border-t border-gray-800">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                        <Users className="w-5 h-5 text-brand-green" />
                        Membres de l'équipe
                      </h2>
                      {/* Only show header CTA when there are existing members to avoid redundancy with empty state CTA */}
                      {teamMembers.length > 0 && (
                        <button
                          onClick={() => setShowInviteModal(true)}
                          className="flex items-center gap-2 px-4 py-2 bg-brand-green text-black font-medium rounded-xl hover:bg-brand-green/90 transition-all"
                        >
                          <UserPlus className="w-4 h-4" />
                          Inviter
                        </button>
                      )}
                    </div>
                    <p className="text-gray-400 text-sm mb-4">
                      Invitez des membres pour accéder aux mêmes workspaces et données de l'entreprise.
                    </p>

                    {/* Team Members List */}
                    <div className="space-y-3">
                      {teamMembers.length === 0 ? (
                        <div className="text-center py-8 bg-[#111111] rounded-xl border border-gray-800">
                          <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                          <p className="text-gray-400">Aucun membre dans l'équipe</p>
                          <p className="text-gray-500 text-sm mb-4">Invitez des membres pour commencer</p>
                          <button
                            onClick={() => setShowInviteModal(true)}
                            className="inline-flex items-center gap-2 text-brand-green hover:text-brand-green/80 font-medium transition-colors"
                          >
                            <UserPlus className="w-4 h-4" />
                            Inviter votre premier membre
                          </button>
                        </div>
                      ) : (
                        teamMembers.map((member) => (
                          <div
                            key={member.id}
                            className="flex items-center justify-between p-4 bg-[#111111] rounded-xl border border-gray-800 hover:border-gray-700 transition-all"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-brand-green/20 flex items-center justify-center text-brand-green font-medium">
                                {member.avatar_url ? (
                                  <img src={member.avatar_url} alt="" className="w-full h-full object-cover rounded-full" />
                                ) : (
                                  (member.first_name?.[0] || member.email[0]).toUpperCase()
                                )}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-white font-medium">
                                    {member.full_name || member.email}
                                  </span>
                                  {member.is_current_user && (
                                    <span className="text-xs bg-brand-green/20 text-brand-green px-2 py-0.5 rounded">Vous</span>
                                  )}
                                  {!member.email_verified && (
                                    <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">En attente</span>
                                  )}
                                </div>
                                <p className="text-gray-400 text-sm">{member.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`px-3 py-1 rounded-lg text-xs font-medium ${
                                member.role === 'admin' || member.role === 'super_admin'
                                  ? 'bg-purple-500/20 text-purple-400'
                                  : member.role === 'manager'
                                  ? 'bg-blue-500/20 text-blue-400'
                                  : 'bg-green-500/20 text-green-400'
                              }`}>
                                {member.role === 'admin' || member.role === 'super_admin' ? 'Admin' :
                                 member.role === 'manager' ? 'Manager' : 'Analyste'}
                              </span>
                              {!member.is_current_user && (
                                <div className="flex items-center gap-1">
                                  {!member.email_verified && (
                                    <button
                                      onClick={async () => {
                                        try {
                                          await account.resendInvitation(member.id);
                                          toast.success('Invitation renvoyée');
                                        } catch (e) {
                                          const msg = e?.response?.data?.detail || 'Erreur lors du renvoi';
                                          toast.error(msg);
                                        }
                                      }}
                                      className="p-2 text-gray-400 hover:text-brand-green hover:bg-brand-green/10 rounded-lg transition-all"
                                      title="Renvoyer l'invitation"
                                    >
                                      <Send className="w-4 h-4" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleRemoveTeamMember(member.id)}
                                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                                    title="Retirer"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Language Section */}
                  <div className="mt-8 pt-8 border-t border-gray-800">
                    <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                      <Languages className="w-5 h-5 text-brand-green" />
                      {t('account.profileSection.language')}
                    </h2>
                    <p className="text-gray-400 text-sm mb-4">
                      {t('account.profileSection.languageDescription')}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                      {LANGUAGES.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            setLanguage(lang.code);
                            setProfile({ ...profile, language: lang.code });
                          }}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                            language === lang.code
                              ? 'bg-brand-green/10 border-brand-green text-white'
                              : 'bg-[#111111] border-gray-800 text-gray-400 hover:border-brand-green/50 hover:bg-brand-green/5'
                          }`}
                        >
                          <span className="text-3xl">{lang.flag}</span>
                          <span className="font-medium flex-1">{t(`account.profileSection.languages.${lang.code}`, lang.name)}</span>
                          <CheckCircle className={`w-4 h-4 ${language === lang.code ? 'text-brand-green' : 'text-transparent'}`} />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-gray-800 flex flex-wrap gap-4">
                    <button
                      onClick={async () => {
                        await handleProfileSave();
                        await handleCompanySave();
                      }}
                      disabled={saving}
                      className="flex items-center gap-2 bg-gradient-to-r from-brand-green to-green-500 hover:from-brand-green/90 hover:to-green-500/90 text-black font-semibold px-4 sm:px-8 py-3 rounded-xl transition-all disabled:opacity-50 shadow-lg shadow-brand-green/20 whitespace-nowrap text-sm sm:text-base"
                    >
                      {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5 flex-shrink-0" />}
                      <span>Tout sauvegarder</span>
                    </button>
                    <button
                      onClick={handleProfileSave}
                      disabled={saving}
                      className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white font-medium px-4 sm:px-6 py-3 rounded-xl transition-all disabled:opacity-50 whitespace-nowrap text-sm sm:text-base"
                    >
                      <User className="w-5 h-5 flex-shrink-0" />
                      <span>Sauvegarder profil</span>
                    </button>
                    <button
                      onClick={handleCompanySave}
                      disabled={saving}
                      className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white font-medium px-4 sm:px-6 py-3 rounded-xl transition-all disabled:opacity-50 whitespace-nowrap text-sm sm:text-base"
                    >
                      <Building2 className="w-5 h-5 flex-shrink-0" />
                      <span>Sauvegarder société</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (() => {
                const currentPlan = subscription?.plan || 'free';
                const canSendSMS = ['enterprise', 'enterprise_plus', 'dedicated', 'groupe'].includes(currentPlan);

                // Password generator function
                const generateSecurePassword = (length = 24, quantumSafe = false) => {
                  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
                  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
                  const numbers = '0123456789';
                  const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
                  const allChars = lowercase + uppercase + numbers + symbols;

                  let password = '';
                  const array = new Uint32Array(length);
                  crypto.getRandomValues(array);

                  // Ensure at least one of each type
                  password += lowercase[array[0] % lowercase.length];
                  password += uppercase[array[1] % uppercase.length];
                  password += numbers[array[2] % numbers.length];
                  password += symbols[array[3] % symbols.length];

                  // Fill the rest
                  for (let i = 4; i < length; i++) {
                    password += allChars[array[i] % allChars.length];
                  }

                  // Shuffle the password
                  const shuffleArray = new Uint32Array(password.length);
                  crypto.getRandomValues(shuffleArray);
                  password = password.split('').sort((a, b) => shuffleArray[password.indexOf(a)] - shuffleArray[password.indexOf(b)]).join('');

                  // For quantum-safe, add extra entropy indicator
                  if (quantumSafe) {
                    password = password.slice(0, length);
                  }

                  return password;
                };

                const handleGeneratePassword = (quantumSafe = false) => {
                  const pwd = generateSecurePassword(quantumSafe ? 32 : 24, quantumSafe);
                  setGeneratedPassword(pwd);
                  setPasswordCopied(false);
                };

                const handleCopyPassword = () => {
                  navigator.clipboard.writeText(generatedPassword);
                  setPasswordCopied(true);
                  toast.success('Mot de passe copié dans le presse-papier');
                  setTimeout(() => setPasswordCopied(false), 3000);
                };

                const handleSendPasswordBySMS = async () => {
                  if (!generatedPassword || !profile.phone) {
                    toast.error('Générez un mot de passe et vérifiez votre numéro de téléphone');
                    return;
                  }
                  setSendingSMS(true);
                  try {
                    // Simulate SMS sending (would call actual API)
                    await new Promise(resolve => setTimeout(resolve, 1500));
                    toast.success('Mot de passe envoyé par SMS');
                  } catch (error) {
                    toast.error('Erreur lors de l\'envoi du SMS');
                  } finally {
                    setSendingSMS(false);
                  }
                };

                const useGeneratedPassword = () => {
                  if (generatedPassword) {
                    setPasswords({ ...passwords, new: generatedPassword, confirm: generatedPassword });
                    toast.success('Mot de passe applique aux champs');
                  }
                };

                return (
                <div className="p-6 space-y-6">
                  {/* Security Warning Banner */}
                  <div className="bg-gradient-to-r from-red-500/10 via-orange-500/10 to-yellow-500/10 rounded-xl p-5 border border-red-500/20">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
                        <AlertCircle className="w-6 h-6 text-red-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-2">Protégez votre compte</h3>
                        <p className="text-gray-400 text-sm leading-relaxed">
                          La sécurité de votre compte est primordiale. Un mot de passe faible ou réutilisé peut compromettre
                          l'ensemble de vos données sensibles et celles de vos clients. <span className="text-red-400 font-medium">80% des violations de données
                          sont liées à des mots de passe compromis.</span> Utilisez notre générateur de mots de passe sécurisés
                          et activez l'authentification à deux facteurs (MFA) pour une protection optimale.
                        </p>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded-full">Phishing</span>
                          <span className="px-2 py-1 bg-orange-500/10 text-orange-400 text-xs rounded-full">Brute Force</span>
                          <span className="px-2 py-1 bg-yellow-500/10 text-yellow-400 text-xs rounded-full">Credential Stuffing</span>
                          <span className="px-2 py-1 bg-purple-500/10 text-purple-400 text-xs rounded-full">Keyloggers</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Password Generator Section */}
                  <div className="bg-gradient-to-br from-[#0d0d0d] to-[#1a1a1a] rounded-xl p-6 border border-cyan-500/20">
                    <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                      <RefreshCw className="w-5 h-5 text-cyan-400" />
                      Generateur de mot de passe securise
                    </h3>
                    <p className="text-gray-400 text-sm mb-4">
                      Générez un mot de passe complexe et unique. Option anti-quantique disponible pour une sécurité future-proof.
                    </p>

                    {/* Generator Buttons */}
                    <div className="flex flex-wrap gap-3 mb-4">
                      <button
                        onClick={() => handleGeneratePassword(false)}
                        className="flex items-center gap-2 px-4 py-2.5 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 font-medium rounded-lg transition-all border border-cyan-500/30"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Générer (24 caractères)
                      </button>
                      <button
                        onClick={() => handleGeneratePassword(true)}
                        className="flex items-center gap-2 px-4 py-2.5 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 font-medium rounded-lg transition-all border border-purple-500/30"
                      >
                        <Shield className="w-4 h-4" />
                        Anti-Quantique (32 caractères)
                      </button>
                    </div>

                    {/* Generated Password Display */}
                    {generatedPassword && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-[#0a0a0a] border border-gray-700 rounded-xl px-4 py-3 font-mono text-brand-green overflow-x-auto">
                            <code className="text-sm break-all">{generatedPassword}</code>
                          </div>
                          <button
                            onClick={handleCopyPassword}
                            className={`p-3 rounded-lg transition-all ${passwordCopied ? 'bg-brand-green text-black' : 'bg-gray-800 hover:bg-gray-700 text-gray-400'}`}
                            title="Copier"
                          >
                            {passwordCopied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                          </button>
                        </div>

                        {/* Password Info */}
                        <div className="flex flex-wrap gap-2">
                          <span className="px-2 py-1 bg-brand-green/10 text-brand-green text-xs rounded-full flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> {generatedPassword.length} caractères
                          </span>
                          <span className="px-2 py-1 bg-blue-500/10 text-blue-400 text-xs rounded-full flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Majuscules + Minuscules
                          </span>
                          <span className="px-2 py-1 bg-yellow-500/10 text-yellow-400 text-xs rounded-full flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Chiffres
                          </span>
                          <span className="px-2 py-1 bg-purple-500/10 text-purple-400 text-xs rounded-full flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Symboles
                          </span>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2 pt-2">
                          <button
                            onClick={useGeneratedPassword}
                            className="flex items-center gap-2 px-4 py-2 bg-brand-green/10 hover:bg-brand-green/20 text-brand-green rounded-lg transition-all text-sm"
                          >
                            <ArrowDownRight className="w-4 h-4" />
                            Utiliser ce mot de passe
                          </button>
                          {canSendSMS && (
                            <button
                              onClick={handleSendPasswordBySMS}
                              disabled={sendingSMS || !profile.phone}
                              className="flex items-center gap-2 px-4 py-2 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 rounded-lg transition-all text-sm disabled:opacity-50"
                            >
                              {sendingSMS ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Smartphone className="w-4 h-4" />
                              )}
                              Envoyer par SMS
                            </button>
                          )}
                        </div>

                        {/* SMS Info for Enterprise */}
                        {canSendSMS && (
                          <p className="text-gray-500 text-xs mt-2">
                            <span className="text-purple-400">Plan Entreprise :</span> Envoyez le mot de passe généré par SMS a votre numéro {profile.phone || '(non configuré)'}
                          </p>
                        )}
                        {!canSendSMS && (
                          <p className="text-gray-500 text-xs mt-2">
                            <Lock className="w-3 h-3 inline mr-1" />
                            Envoi par SMS disponible avec les plans Entreprise et superieur
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Password Change Section */}
                  <div className="bg-[#0d0d0d] rounded-xl p-6 border border-gray-800">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Lock className="w-5 h-5 text-brand-green" />
                      Modifier votre mot de passe
                    </h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="relative">
                        <label className="block text-sm text-gray-400 mb-2">Mot de passe actuel</label>
                        <input
                          type={showPasswords ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={passwords.current}
                          onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                          className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                   focus:outline-none focus:border-brand-green/50 pr-10"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Nouveau mot de passe</label>
                        <input
                          type={showPasswords ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={passwords.new}
                          onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                          className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                   focus:outline-none focus:border-brand-green/50"
                        />
                      </div>
                      <div className="relative">
                        <label className="block text-sm text-gray-400 mb-2">Confirmer</label>
                        <input
                          type={showPasswords ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={passwords.confirm}
                          onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                          className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                                   focus:outline-none focus:border-brand-green/50 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(!showPasswords)}
                          className="absolute right-3 top-[38px] text-gray-500 hover:text-gray-300"
                        >
                          {showPasswords ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-4">
                      <button
                        onClick={handlePasswordChange}
                        disabled={saving || !passwords.current || !passwords.new}
                        className="flex items-center gap-2 bg-brand-green hover:bg-brand-green/90 text-black font-semibold px-5 py-2.5 rounded-lg transition-all disabled:opacity-50"
                      >
                        <Save className="w-4 h-4" />
                        Enregistrer le nouveau mot de passe
                      </button>
                      {passwords.new && passwords.new === passwords.confirm && (
                        <span className="flex items-center gap-1 text-brand-green text-sm">
                          <CheckCircle className="w-4 h-4" /> Mots de passe identiques
                        </span>
                      )}
                      {passwords.new && passwords.confirm && passwords.new !== passwords.confirm && (
                        <span className="flex items-center gap-1 text-red-400 text-sm">
                          <AlertCircle className="w-4 h-4" /> Les mots de passe ne correspondent pas
                        </span>
                      )}
                    </div>
                  </div>

                  {/* MFA Section */}
                  <div className="bg-[#0d0d0d] rounded-xl p-6 border border-gray-800">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                          <Smartphone className="w-5 h-5 text-brand-green" />
                          Authentification à deux facteurs (MFA)
                        </h3>
                        <p className="text-gray-400 text-sm mt-1">
                          Utilisez Google Authenticator, Authy ou Microsoft Authenticator pour sécuriser votre compte.
                          <span className="text-brand-green"> Fortement recommandé.</span>
                        </p>
                      </div>
                      {mfaEnabled ? (
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-green/10 text-brand-green text-sm font-medium">
                            <CheckCircle className="w-4 h-4" />
                            Active
                          </span>
                          <button
                            onClick={handleMfaDisable}
                            className="text-red-400 hover:text-red-300 text-sm"
                          >
                            Desactiver
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={handleMfaSetup}
                          className="flex items-center gap-2 bg-brand-green hover:bg-brand-green/90 text-black font-semibold px-4 py-2 rounded-lg transition-all"
                        >
                          <Smartphone className="w-4 h-4" />
                          Activer MFA
                        </button>
                      )}
                    </div>
                  </div>

                  {/* MFA Reminder Banner */}
                  {!mfaEnabled && (
                    <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl p-5 border border-amber-500/30">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0 animate-pulse">
                          <Shield className="w-6 h-6 text-amber-400" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-amber-400 font-semibold mb-1">Activez l'authentification à deux facteurs (MFA)</h4>
                          <p className="text-gray-400 text-sm">
                            Votre compte n'est pas protégé par le MFA. Cette fonctionnalité ajoute une couche de sécurité
                            essentielle en exigeant un code temporaire en plus de votre mot de passe.
                            <span className="text-amber-400 font-medium"> Les comptes sans MFA sont 99% plus vulnérables aux attaques.</span>
                          </p>
                          <button
                            onClick={handleMfaSetup}
                            className="mt-3 flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold px-4 py-2 rounded-lg transition-all"
                          >
                            <Smartphone className="w-4 h-4" />
                            Activer maintenant
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Team Collaboration Section - Enterprise Only */}
                  {(() => {
                    const canManageTeam = ['enterprise', 'enterprise_plus', 'dedicated', 'groupe'].includes(currentPlan);
                    // Initialize team members with current user if empty
                    const teamMembersList = securityTeamMembers.length > 0 ? securityTeamMembers : [
                      { id: 1, name: 'Vous', email: profile.email, role: 'admin', status: 'active', avatar: null, isOwner: true },
                    ];

                    const handleInviteMember = async () => {
                      if (!inviteEmail) return;
                      setInviting(true);
                      try {
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        setSecurityTeamMembers([...teamMembersList, {
                          id: Date.now(),
                          name: inviteEmail.split('@')[0],
                          email: inviteEmail,
                          role: inviteRole,
                          status: 'pending',
                          avatar: null,
                          isOwner: false
                        }]);
                        setInviteEmail('');
                        toast.success('Invitation envoyée par email');
                      } catch (error) {
                        toast.error('Erreur lors de l\'envoi');
                      } finally {
                        setInviting(false);
                      }
                    };

                    const handleRemoveMember = (memberId) => {
                      setSecurityTeamMembers(teamMembersList.filter(m => m.id !== memberId));
                      toast.success('Collaborateur retire');
                    };

                    const roleLabels = {
                      admin: { label: 'Administrateur', color: 'text-purple-400 bg-purple-500/10' },
                      manager: { label: 'Manager', color: 'text-blue-400 bg-blue-500/10' },
                      analyst: { label: 'Analyste', color: 'text-brand-green bg-brand-green/10' },
                      viewer: { label: 'Lecteur', color: 'text-gray-400 bg-gray-500/10' }
                    };

                    return (
                      <div className={`bg-gradient-to-br from-[#0d0d0d] to-[#1a1a2e] rounded-xl p-6 border ${canManageTeam ? 'border-blue-500/30' : 'border-gray-800'}`}>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-xl ${canManageTeam ? 'bg-blue-500/20' : 'bg-gray-700'} flex items-center justify-center`}>
                              <Users className={`w-6 h-6 ${canManageTeam ? 'text-blue-400' : 'text-gray-500'}`} />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-white">Collaborateurs</h3>
                              <p className={`text-sm ${canManageTeam ? 'text-blue-400' : 'text-gray-500'}`}>
                                Travaillez en équipe sur vos audits
                              </p>
                            </div>
                          </div>
                          {canManageTeam ? (
                            <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs font-medium rounded-full">
                              {teamMembersList.length} membre{teamMembersList.length > 1 ? 's' : ''}
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-gray-700 text-gray-400 text-xs font-medium rounded-full flex items-center gap-1">
                              <Lock className="w-3 h-3" /> Entreprise+
                            </span>
                          )}
                        </div>

                        {canManageTeam ? (
                          <>
                            <p className="text-gray-400 text-sm mb-4">
                              Invitez des membres à rejoindre votre espace de travail. Ils pourront consulter les scans,
                              les vulnérabilités et collaborer sur les audits de sécurité selon leur rôle.
                            </p>

                            {/* Invite Form */}
                            <div className="flex gap-2 mb-4">
                              <input
                                type="email"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                placeholder="membre@exemple.fr"
                                className="flex-1 bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500/50"
                              />
                              <select
                                value={inviteRole}
                                onChange={(e) => setInviteRole(e.target.value)}
                                className="bg-[#0a0a0a] border border-gray-700 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500/50"
                              >
                                <option value="analyst">Analyste</option>
                                <option value="manager">Manager</option>
                                <option value="admin">Admin</option>
                                <option value="viewer">Lecteur</option>
                              </select>
                              <button
                                onClick={handleInviteMember}
                                disabled={!inviteEmail || inviting}
                                className="flex items-center gap-2 px-4 py-2.5 bg-blue-500 hover:bg-blue-400 text-white font-medium rounded-lg transition-all disabled:opacity-50"
                              >
                                {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                                Inviter
                              </button>
                            </div>

                            {/* Team Members List */}
                            <div className="space-y-2">
                              {teamMembersList.map((member) => (
                                <div key={member.id} className="flex items-center justify-between p-3 bg-[#0a0a0a] rounded-lg border border-gray-800">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 flex items-center justify-center text-white font-medium text-sm">
                                      {member.name[0].toUpperCase()}
                                    </div>
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-white text-sm font-medium">{member.name}</span>
                                        {member.isOwner && (
                                          <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-[10px] rounded">Propriétaire</span>
                                        )}
                                        {member.status === 'pending' && (
                                          <span className="px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 text-[10px] rounded">En attente</span>
                                        )}
                                      </div>
                                      <span className="text-gray-500 text-xs">{member.email}</span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${roleLabels[member.role]?.color || ''}`}>
                                      {roleLabels[member.role]?.label || member.role}
                                    </span>
                                    {!member.isOwner && (
                                      <button
                                        onClick={() => handleRemoveMember(member.id)}
                                        className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-all"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* Permissions Info */}
                            <div className="mt-4 p-3 bg-blue-500/5 rounded-lg border border-blue-500/20">
                              <p className="text-gray-400 text-xs">
                                <strong className="text-blue-400">Rôles :</strong> Admin (tous droits) • Manager (scans + rapports) • Analyste (consultation + annotations) • Lecteur (lecture seule)
                              </p>
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-6">
                            <p className="text-gray-400 text-sm mb-4">
                              La collaboration en équipe permet d'inviter des collègues à travailler ensemble sur vos audits de sécurité.
                              Partagez les scans, assignez des vulnérabilités et collaborez en temps réel.
                            </p>
                            <button
                              onClick={() => setActiveTab('subscription')}
                              className="flex items-center gap-2 mx-auto px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
                            >
                              <Zap className="w-4 h-4" />
                              Voir mon abonnement
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                );
              })()}

              {/* Contract Tab */}
              {activeTab === 'contract' && (
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                    <FileSignature className="w-5 h-5 text-brand-green" />
                    Contrats d'autorisation de Pentest
                  </h2>

                  <p className="text-gray-400 mb-6">
                    Gérez vos contrats d'autorisation de test d'intrusion avec signature electronique.
                    Ces contrats sont indispensables avant tout audit de sécurité.
                  </p>

                  {/* Contract Manager Component */}
                  <ContractManager />

                  {/* Legal docs */}
                  <h3 className="text-lg font-semibold text-white mb-4 mt-8">Documents légaux</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-[#0d0d0d] rounded-xl p-5 border border-gray-800">
                      <h4 className="text-white font-medium mb-2">Conditions générales d'utilisation</h4>
                      <p className="text-gray-500 text-sm mb-3">Version 2.0 - Décembre 2025</p>
                      <button className="text-brand-green hover:text-brand-green/80 text-sm font-medium">
                        Consulter les CGU
                      </button>
                    </div>
                    <div className="bg-[#0d0d0d] rounded-xl p-5 border border-gray-800">
                      <h4 className="text-white font-medium mb-2">Politique de confidentialite</h4>
                      <p className="text-gray-500 text-sm mb-3">Conforme RGPD</p>
                      <button className="text-brand-green hover:text-brand-green/80 text-sm font-medium">
                        Consulter la politique
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Reports Tab */}
              {activeTab === 'reports' && (
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-brand-green" />
                    Configuration des rapports
                  </h2>

                  <p className="text-gray-400 mb-6">
                    Configurez les types de rapports disponibles, personnalisez leur contenu, branding et informations société.
                  </p>

                  {/* Embedded Report Types Panel */}
                  <ReportTypesPanel embedded={true} />
                </div>
              )}

              {/* Subscription Tab */}
              {activeTab === 'subscription' && (
                <div className="p-6">
                  {/* Current Plan Header */}
                  <div className={`rounded-xl p-6 ${planStyle.bg} border ${planStyle.border} mb-6`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-xl ${planStyle.bg} flex items-center justify-center border ${planStyle.border}`}>
                          <PlanIcon className={`w-7 h-7 ${planStyle.color}`} />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">{subscription?.plan_name || 'Plan Gratuit'}</h3>
                          <p className="text-gray-400 text-sm">
                            {subscription?.status === 'active' ? 'Abonnement actif' : 'Mode demonstration'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-2 bg-brand-green/10 text-brand-green font-semibold px-4 py-2 rounded-xl border border-brand-green/30">
                          <CheckCircle className="w-4 h-4" />
                          Actif
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Quotas & Usage */}
                  <h3 className="text-lg font-semibold text-white mb-4">Utilisation & Quotas</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Clients/Workspaces */}
                    <div className="bg-[#0d0d0d] rounded-xl p-5 border border-gray-800">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Clients</p>
                          <p className="text-white font-semibold">
                            {formatQuota(subscription?.quotas?.max_workspaces || 1, workspacesCount).text}
                          </p>
                        </div>
                      </div>
                      {subscription?.quotas?.max_workspaces !== -1 && (
                        <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full transition-all"
                            style={{ width: `${formatQuota(subscription?.quotas?.max_workspaces || 1, workspacesCount).percent}%` }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Users */}
                    <div className="bg-[#0d0d0d] rounded-xl p-5 border border-gray-800">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                          <Users className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Utilisateurs</p>
                          <p className="text-white font-semibold">
                            {subscription?.quotas?.max_users === -1 ? 'Illimite' : `${teamMembers.length || 1} / ${subscription?.quotas?.max_users || 1}`}
                          </p>
                        </div>
                      </div>
                      {subscription?.quotas?.max_users !== -1 && (
                        <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-purple-500 rounded-full transition-all"
                            style={{ width: `${((teamMembers.length || 1) / (subscription?.quotas?.max_users || 1)) * 100}%` }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Targets */}
                    <div className="bg-[#0d0d0d] rounded-xl p-5 border border-gray-800">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                          <Target className="w-5 h-5 text-orange-400" />
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Cibles</p>
                          <p className="text-white font-semibold">
                            {subscription?.quotas?.max_targets === -1
                              ? `${targetsUsed} (Illimite)`
                              : `${targetsUsed} / ${subscription?.quotas?.max_targets || 1}`}
                          </p>
                        </div>
                      </div>
                      {subscription?.quotas?.max_targets !== -1 && subscription?.quotas?.max_targets > 0 && (
                        <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-orange-500 rounded-full transition-all"
                            style={{ width: `${Math.min((targetsUsed / (subscription?.quotas?.max_targets || 1)) * 100, 100)}%` }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Scans */}
                    <div className="bg-[#0d0d0d] rounded-xl p-5 border border-gray-800">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-brand-green/10 flex items-center justify-center">
                          <Scan className="w-5 h-5 text-brand-green" />
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Scans ce mois</p>
                          <p className="text-white font-semibold">
                            {subscription?.quotas?.max_scans_per_month === -1
                              ? `${usage?.scans || 0} (Illimite)`
                              : `${usage?.scans || 0} / ${subscription?.quotas?.max_scans_per_month || 0}`}
                          </p>
                        </div>
                      </div>
                      {subscription?.quotas?.max_scans_per_month !== -1 && subscription?.quotas?.max_scans_per_month > 0 && (
                        <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-brand-green rounded-full transition-all"
                            style={{ width: `${Math.min(((usage?.scans || 0) / subscription?.quotas?.max_scans_per_month) * 100, 100)}%` }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Contracts */}
                    <div className="bg-[#0d0d0d] rounded-xl p-5 border border-gray-800">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                          <FileCheck className="w-5 h-5 text-cyan-400" />
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Contrats</p>
                          <p className="text-white font-semibold">
                            {subscription?.quotas?.max_contracts === -1 ? 'Illimite' : `0 / ${subscription?.quotas?.max_contracts || 0}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Abonnement Serveur Dédié */}
                  <h3 className="text-lg font-semibold text-white mt-8 mb-4 flex items-center gap-2">
                    <Server className="w-5 h-5 text-orange-400" />
                    Abonnement
                  </h3>
                  <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 rounded-xl p-6 border border-orange-500/20 mb-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-xl bg-orange-500/20 flex items-center justify-center">
                        <Server className="w-7 h-7 text-orange-400" />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-white">Serveur Dédié XS</p>
                        <p className="text-gray-400 text-sm">Infrastructure dédiée mono-client avec IA locale</p>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="bg-black/20 rounded-xl p-4">
                        <p className="text-gray-400 text-sm mb-2">Infrastructure</p>
                        <ul className="space-y-1 text-sm text-white">
                          <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-brand-green flex-shrink-0" />Serveur dédié isolé</li>
                          <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-brand-green flex-shrink-0" />CPU multi-coeurs</li>
                          <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-brand-green flex-shrink-0" />IA locale sur CPU (Qwen3 8B)</li>
                          <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-brand-green flex-shrink-0" />Tous outils pentest inclus</li>
                        </ul>
                      </div>
                      <div className="bg-black/20 rounded-xl p-4">
                        <p className="text-gray-400 text-sm mb-2">Inclus dans votre plan</p>
                        <ul className="space-y-1 text-sm text-white">
                          <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-brand-green flex-shrink-0" />100 opérations / mois</li>
                          <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-brand-green flex-shrink-0" />10 utilisateurs max</li>
                          <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-brand-green flex-shrink-0" />100 cibles max</li>
                          <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-brand-green flex-shrink-0" />Support prioritaire</li>
                        </ul>
                      </div>
                    </div>

                    {/* Upsell */}
                    <div className="border-t border-orange-500/20 pt-4">
                      <p className="text-gray-400 text-sm mb-3">Besoin de plus de puissance ?</p>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="bg-black/30 rounded-xl p-4 border border-gray-800">
                          <div className="flex items-center gap-2 mb-1">
                            <Sparkles className="w-4 h-4 text-purple-400" />
                            <span className="text-white font-semibold">Dédié L</span>
                          </div>
                          <p className="text-gray-400 text-xs">GPU dédié pour IA accélérée, modèles 70B+</p>
                        </div>
                        <div className="bg-black/30 rounded-xl p-4 border border-gray-800">
                          <div className="flex items-center gap-2 mb-1">
                            <Sparkles className="w-4 h-4 text-yellow-400" />
                            <span className="text-white font-semibold">Dédié XL</span>
                          </div>
                          <p className="text-gray-400 text-xs">Double GPU, performances maximales, opérations illimitées</p>
                        </div>
                      </div>
                      <p className="text-gray-500 text-xs mt-3">Contact : 01.39.81.05.74 | support@sylink.fr</p>
                    </div>
                  </div>

                  {/* Features */}
                  <h3 className="text-lg font-semibold text-white mt-8 mb-4">Fonctionnalités incluses</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {[
                      { key: 'ai_scan', label: 'Scan Auto IA', freeIncluded: true },
                      { key: 'expert_scan', label: 'Analyse Experte' },
                      { key: 'api_scan', label: 'Scan d\'API' },
                      { key: 'ot_scan', label: 'Scan OT/SCADA' },
                      { key: 'iot_scan', label: 'Scan IoT' },
                      { key: 'vpn_scan', label: 'Scan VPN' },
                      { key: 'credential_search', label: 'Recherche credentials' },
                      { key: 'scheduling', label: 'Planification' },
                      { key: 'mfa', label: 'Double authentification', freeIncluded: true },
                      { key: 'custom_reports', label: 'Rapports personnalises' },
                      { key: 'priority_support', label: 'Support prioritaire' }
                    ].map((feature) => {
                      // Fonctionnalites incluses par defaut pour tous les plans (y compris Free)
                      const enabled = feature.freeIncluded || subscription?.features?.[feature.key];
                      return (
                        <div
                          key={feature.key}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                            enabled ? 'bg-brand-green/5 border border-brand-green/20' : 'bg-gray-800/30 border border-gray-800'
                          }`}
                        >
                          <CheckCircle className={`w-5 h-5 ${enabled ? 'text-brand-green' : 'text-gray-600'}`} />
                          <span className={enabled ? 'text-white' : 'text-gray-500'}>{feature.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}


              {/* Support Tab */}
              {activeTab === 'support' && (
                <div className="p-6 text-center">
                  <div className="max-w-md mx-auto py-8">
                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-brand-green/10 flex items-center justify-center">
                      <HelpCircle className="w-8 h-8 text-brand-green" />
                    </div>
                    <h2 className="text-xl font-semibold text-white mb-2">Besoin d'aide ?</h2>
                    <p className="text-gray-400 mb-8">
                      Notre équipe est là pour vous aider. Consultez vos tickets ou créez-en un nouveau.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <a
                        href="/support/tickets"
                        className="flex items-center justify-center gap-2 bg-brand-green hover:bg-brand-green/90 text-black font-semibold px-6 py-3 rounded-xl transition-all"
                      >
                        <FileText className="w-5 h-5" />
                        Voir mes tickets
                      </a>
                      <a
                        href="/support/tickets/new"
                        className="flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-white font-semibold px-6 py-3 rounded-xl transition-all"
                      >
                        <HelpCircle className="w-5 h-5" />
                        Nouveau ticket
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Team Tab (Enterprise+) */}
              {activeTab === 'team' && isEnterprisePlan && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                      <Users className="w-5 h-5 text-brand-green" />
                      Gestion des utilisateurs
                    </h2>
                    {/* Only show header CTA when there are existing members to avoid redundancy with empty state CTA */}
                    {teamMembers.length > 0 && (
                      <button
                        onClick={() => setShowInviteModal(true)}
                        className="flex items-center gap-2 bg-brand-green hover:bg-brand-green/90 text-black font-semibold px-4 py-2 rounded-lg transition-all"
                      >
                        <UserPlus className="w-4 h-4" />
                        Inviter un membre
                      </button>
                    )}
                  </div>

                  <p className="text-gray-400 mb-6">
                    Gérez les membres de votre équipe et leurs droits d'accès.
                    Quota : {teamMembers.length} / {subscription?.quotas?.max_users === -1 ? 'Illimité' : subscription?.quotas?.max_users || 5}
                  </p>

                  <div className="space-y-3">
                    {teamMembers.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between bg-[#0d0d0d] rounded-xl p-4 border border-gray-800 hover:border-gray-700 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-green/30 to-brand-green/10 flex items-center justify-center text-brand-green font-bold border border-brand-green/30 overflow-hidden">
                            {member.avatar_url ? (
                              <img src={member.avatar_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              (member.first_name?.[0] || member.email[0]).toUpperCase()
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-white font-medium">
                                {member.full_name || member.email}
                              </p>
                              {member.is_current_user && (
                                <span className="text-xs bg-brand-green/10 text-brand-green px-2 py-0.5 rounded">Vous</span>
                              )}
                              {!member.email_verified && (
                                <span className="text-xs bg-yellow-500/10 text-yellow-400 px-2 py-0.5 rounded">En attente</span>
                              )}
                            </div>
                            <p className="text-gray-400 text-sm">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            member.role === 'company_admin' || member.role === 'admin'
                              ? 'bg-purple-500/10 text-purple-400'
                              : member.role === 'manager'
                              ? 'bg-blue-500/10 text-blue-400'
                              : 'bg-gray-800 text-gray-400'
                          }`}>
                            {member.role === 'company_admin' || member.role === 'admin' ? 'Admin' :
                             member.role === 'manager' ? 'Manager' : 'Analyste'}
                          </span>
                          {!member.is_current_user && (
                            <div className="flex items-center gap-1">
                              {!member.email_verified && (
                                <button
                                  onClick={() => handleResendInvitation(member.id)}
                                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                                  title="Renvoyer l'invitation"
                                >
                                  <Send className="w-4 h-4 text-gray-400" />
                                </button>
                              )}
                              <button
                                onClick={() => handleRemoveTeamMember(member.id)}
                                className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                                title="Retirer"
                              >
                                <Trash2 className="w-4 h-4 text-red-400" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {teamMembers.length === 0 && (
                      <div className="text-center py-12">
                        <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-400">Aucun membre dans l'équipe</p>
                        <button
                          onClick={() => setShowInviteModal(true)}
                          className="mt-4 text-brand-green hover:text-brand-green/80 font-medium"
                        >
                          Inviter votre premier membre
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* History Tab */}
              {activeTab === 'history' && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                      <History className="w-5 h-5 text-brand-green" />
                      Historique d'activité
                    </h2>
                    <button
                      onClick={loadActivityHistory}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                      disabled={historyLoading}
                    >
                      <RefreshCw className={`w-4 h-4 ${historyLoading ? 'animate-spin' : ''}`} />
                      Actualiser
                    </button>
                  </div>

                  {/* Summary cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-[#0d0d0d] rounded-xl p-4 border border-gray-800">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-brand-green/10 flex items-center justify-center">
                          <User className="w-5 h-5 text-brand-green" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider">Dernière connexion</p>
                          <p className="text-white font-medium">
                            {profile.last_login_at ? new Date(profile.last_login_at).toLocaleString('fr-FR') : 'Jamais'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-[#0d0d0d] rounded-xl p-4 border border-gray-800">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider">Compte créé le</p>
                          <p className="text-white font-medium">
                            {profile.created_at ? new Date(profile.created_at).toLocaleDateString('fr-FR') : '-'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-[#0d0d0d] rounded-xl p-4 border border-gray-800">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${mfaEnabled ? 'bg-brand-green/10' : 'bg-yellow-500/10'} flex items-center justify-center`}>
                          <Shield className={`w-5 h-5 ${mfaEnabled ? 'text-brand-green' : 'text-yellow-400'}`} />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider">MFA</p>
                          <p className={`font-medium ${mfaEnabled ? 'text-brand-green' : 'text-yellow-400'}`}>
                            {mfaEnabled ? 'Activée' : 'Désactivée'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Activity Timeline */}
                  <div className="bg-[#0d0d0d] rounded-xl border border-gray-800 overflow-hidden">
                    <div className="p-4 border-b border-gray-800 flex items-center justify-between">
                      <h3 className="text-white font-medium">Journal des activités</h3>
                      <span className="text-xs text-gray-500">{activityHistory.length} événements</span>
                    </div>

                    {historyLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
                      </div>
                    ) : activityHistory.length === 0 ? (
                      <div className="text-center py-12">
                        <History className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-400">Aucune activité enregistrée</p>
                        <p className="text-gray-500 text-sm mt-1">Les actions sur votre compte apparaîtront ici</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-800 max-h-[600px] overflow-y-auto">
                        {activityHistory.map((action, index) => {
                          // Determine icon and color based on action type
                          const getActionStyle = (actionType, category) => {
                            const styles = {
                              // Authentication
                              'login': { icon: User, color: 'text-brand-green', bg: 'bg-brand-green/10' },
                              'logout': { icon: User, color: 'text-gray-400', bg: 'bg-gray-500/10' },
                              'password_reset': { icon: Key, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
                              'password_change': { icon: Lock, color: 'text-blue-400', bg: 'bg-blue-500/10' },
                              'mfa_enabled': { icon: Shield, color: 'text-brand-green', bg: 'bg-brand-green/10' },
                              'mfa_disabled': { icon: Shield, color: 'text-red-400', bg: 'bg-red-500/10' },
                              'email_verified': { icon: Mail, color: 'text-brand-green', bg: 'bg-brand-green/10' },
                              'account_created': { icon: CheckCircle, color: 'text-brand-green', bg: 'bg-brand-green/10' },
                              // Navigation
                              'page_view': { icon: Globe, color: 'text-blue-400', bg: 'bg-blue-500/10' },
                              // Scans
                              'scan_started': { icon: Target, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
                              'scan_completed': { icon: CheckCircle, color: 'text-brand-green', bg: 'bg-brand-green/10' },
                              'scan_failed': { icon: AlertCircle, color: 'text-red-400', bg: 'bg-red-500/10' },
                              'scan_cancelled': { icon: AlertCircle, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
                              // Reports
                              'report_generated': { icon: FileText, color: 'text-purple-400', bg: 'bg-purple-500/10' },
                              'report_downloaded': { icon: Download, color: 'text-blue-400', bg: 'bg-blue-500/10' },
                              // Settings
                              'settings_updated': { icon: RefreshCw, color: 'text-blue-400', bg: 'bg-blue-500/10' },
                              'profile_updated': { icon: User, color: 'text-blue-400', bg: 'bg-blue-500/10' },
                              // Workspaces
                              'workspace_created': { icon: Building2, color: 'text-purple-400', bg: 'bg-purple-500/10' },
                              'workspace_updated': { icon: Building2, color: 'text-blue-400', bg: 'bg-blue-500/10' },
                              // Credentials
                              'credential_added': { icon: Key, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
                              // Tutorial
                              'tutorial_completed': { icon: CheckCircle, color: 'text-brand-green', bg: 'bg-brand-green/10' },
                              'tutorial_skipped': { icon: ChevronRight, color: 'text-gray-400', bg: 'bg-gray-500/10' },
                            };
                            return styles[actionType] || { icon: History, color: 'text-gray-400', bg: 'bg-gray-500/10' };
                          };

                          const style = getActionStyle(action.action_type, action.action_category);
                          const IconComponent = style.icon;

                          // Get readable action description
                          const getActionDescription = (action) => {
                            const descriptions = {
                              'login': 'Connexion au compte',
                              'logout': 'Deconnexion',
                              'password_reset': 'Réinitialisation du mot de passe',
                              'password_change': 'Changement de mot de passe',
                              'mfa_enabled': 'Activation de l\'authentification 2FA',
                              'mfa_disabled': 'Désactivation de l\'authentification 2FA',
                              'email_verified': 'Adresse e-mail vérifiée',
                              'account_created': 'Compte créé',
                              'page_view': `Page consultée: ${action.page_title || action.page || 'Page'}`,
                              'scan_started': `Scan démarré${action.metadata?.target ? ` sur ${action.metadata.target}` : ''}`,
                              'scan_completed': `Scan terminé${action.metadata?.target ? ` sur ${action.metadata.target}` : ''}`,
                              'scan_failed': `Scan échoué${action.metadata?.target ? ` sur ${action.metadata.target}` : ''}`,
                              'scan_cancelled': `Scan annulé`,
                              'report_generated': `Rapport généré${action.metadata?.report_type ? ` (${action.metadata.report_type})` : ''}`,
                              'report_downloaded': 'Rapport téléchargé',
                              'settings_updated': 'Paramètres mis à jour',
                              'profile_updated': 'Profil mis à jour',
                              'workspace_created': `Workspace créé${action.metadata?.name ? `: ${action.metadata.name}` : ''}`,
                              'workspace_updated': 'Workspace modifié',
                              'credential_added': 'Identifiant ajouté',
                              'tutorial_completed': 'Tutoriel terminé',
                              'tutorial_skipped': 'Tutoriel ignoré',
                            };
                            return descriptions[action.action_type] || action.action_type?.replace(/_/g, ' ') || 'Action';
                          };

                          return (
                            <div key={action.id || index} className="flex items-start gap-4 p-4 hover:bg-gray-800/30 transition-colors">
                              <div className={`w-10 h-10 rounded-lg ${style.bg} flex items-center justify-center shrink-0`}>
                                <IconComponent className={`w-5 h-5 ${style.color}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-white font-medium">
                                  {getActionDescription(action)}
                                </p>
                                <div className="flex items-center gap-3 mt-1">
                                  <span className="text-xs text-gray-500">
                                    {action.created_at ? new Date(action.created_at).toLocaleString('fr-FR', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    }) : '-'}
                                  </span>
                                  {action.ip_address && (
                                    <span className="text-xs text-gray-600">
                                      IP : {action.ip_address}
                                    </span>
                                  )}
                                  {action.user_agent && (
                                    <span className="text-xs text-gray-600 truncate max-w-[200px]" title={action.user_agent}>
                                      {action.user_agent.includes('Chrome') ? 'Chrome' :
                                       action.user_agent.includes('Firefox') ? 'Firefox' :
                                       action.user_agent.includes('Safari') ? 'Safari' :
                                       action.user_agent.includes('Edge') ? 'Edge' : 'Browser'}
                                    </span>
                                  )}
                                </div>
                                {action.metadata && Object.keys(action.metadata).length > 0 && (
                                  <div className="mt-2 text-xs text-gray-500 bg-gray-800/50 rounded px-2 py-1 inline-block">
                                    {JSON.stringify(action.metadata).substring(0, 100)}
                                    {JSON.stringify(action.metadata).length > 100 ? '...' : ''}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}


              {/* Integrations Tab */}
              {activeTab === 'integrations' && (() => {
                // Full integrations list with wizards
                const integrationsList = [
                  // SIEM & Security
                  { id: 'splunk', name: 'Splunk SIEM', description: 'Export des evenements vers Splunk HEC', icon: '📡', wizard: 'splunk', category: 'SIEM' },
                  { id: 'elasticsearch', name: 'Elasticsearch', description: 'Indexation des scans et recherche', icon: '🔍', wizard: null, category: 'SIEM' },
                  { id: 'wazuh', name: 'Wazuh', description: 'Integration SIEM Wazuh open-source', icon: '🛡️', wizard: null, category: 'SIEM' },
                  { id: 'sekoia', name: 'Sekoia.io', description: 'Plateforme XDR SecOps', icon: '🔐', wizard: null, category: 'SIEM' },
                  { id: 'qradar', name: 'IBM QRadar', description: 'SIEM et analyse de sécurité IBM', icon: '🔷', wizard: null, category: 'SIEM' },
                  { id: 'logrhythm', name: 'LogRhythm', description: 'SIEM NextGen avec SOAR intégré', icon: '📋', wizard: null, category: 'SIEM' },
                  { id: 'sentinel', name: 'Microsoft Sentinel', description: 'SIEM cloud-native Azure', icon: '☁️', wizard: null, category: 'SIEM' },
                  { id: 'arcsight', name: 'ArcSight', description: 'SIEM enterprise Micro Focus', icon: '🎯', wizard: null, category: 'SIEM' },
                  { id: 'alienvault', name: 'AlienVault USM', description: 'Unified Security Management', icon: '👽', wizard: null, category: 'SIEM' },
                  { id: 'graylog', name: 'Graylog', description: 'Log management open-source', icon: '📝', wizard: null, category: 'SIEM' },

                  // Ticketing
                  { id: 'jira', name: 'Jira', description: 'Creation automatique de tickets', icon: '🎫', wizard: 'jira', category: 'Ticketing' },
                  { id: 'glpi', name: 'GLPI', description: 'Gestion de parc et tickets ITSM', icon: '🖥️', wizard: null, category: 'Ticketing' },
                  { id: 'servicenow', name: 'ServiceNow', description: 'Plateforme ITSM enterprise', icon: '⚡', wizard: null, category: 'Ticketing' },
                  { id: 'zendesk', name: 'Zendesk', description: 'Support client et ticketing', icon: '🎧', wizard: null, category: 'Ticketing' },
                  { id: 'freshservice', name: 'Freshservice', description: 'ITSM moderne et intuitif', icon: '🍀', wizard: null, category: 'Ticketing' },
                  { id: 'redmine', name: 'Redmine', description: 'Gestion de projet open-source', icon: '🔴', wizard: null, category: 'Ticketing' },
                  { id: 'mantis', name: 'MantisBT', description: 'Bug tracker open-source', icon: '🐛', wizard: null, category: 'Ticketing' },
                  { id: 'otrs', name: 'OTRS', description: 'Service management enterprise', icon: '📨', wizard: null, category: 'Ticketing' },

                  // ChatOps
                  { id: 'slack', name: 'Slack', description: 'Notifications en temps réel', icon: '💬', wizard: 'slack', category: 'ChatOps' },
                  { id: 'teams', name: 'Microsoft Teams', description: 'Alertes dans vos canaux Teams', icon: '💼', wizard: 'teams', category: 'ChatOps' },
                  { id: 'discord', name: 'Discord', description: 'Webhooks Discord pour alertes', icon: '🎮', wizard: 'discord', category: 'ChatOps' },
                  { id: 'mattermost', name: 'Mattermost', description: 'Chat open-source auto-heberge', icon: '💬', wizard: 'mattermost', category: 'ChatOps' },
                  { id: 'telegram', name: 'Telegram Bot', description: 'Alertes via bot Telegram', icon: '✈️', wizard: null, category: 'ChatOps' },
                  { id: 'rocketchat', name: 'Rocket.Chat', description: 'Chat team open-source', icon: '🚀', wizard: null, category: 'ChatOps' },

                  // Notifications
                  { id: 'email', name: 'Email SMTP', description: 'Notifications par email personnalise', icon: '📧', wizard: 'email', category: 'Notifications' },
                  { id: 'sms', name: 'SMS (Twilio)', description: 'Alertes SMS critiques', icon: '📱', wizard: null, category: 'Notifications' },
                  { id: 'pagerduty', name: 'PagerDuty', description: 'Gestion des incidents et astreintes', icon: '🚨', wizard: null, category: 'Notifications' },
                  { id: 'opsgenie', name: 'OpsGenie', description: 'Alerting et on-call management', icon: '👁️', wizard: null, category: 'Notifications' },
                  { id: 'victorops', name: 'VictorOps', description: 'Incident management DevOps', icon: '⚔️', wizard: null, category: 'Notifications' },

                  // Vulnerability Management
                  { id: 'tenable', name: 'Tenable.io', description: 'Import/export vulnérabilités', icon: '🔍', wizard: null, category: 'Vulnerability' },
                  { id: 'qualys', name: 'Qualys', description: 'Integration scanner Qualys', icon: '🛡️', wizard: null, category: 'Vulnerability' },
                  { id: 'rapid7', name: 'Rapid7 InsightVM', description: 'Gestion des vulnérabilités', icon: '⚡', wizard: null, category: 'Vulnerability' },
                  { id: 'nessus', name: 'Nessus', description: 'Import resultats Nessus', icon: '🎯', wizard: null, category: 'Vulnerability' },
                  { id: 'defectdojo', name: 'DefectDojo', description: 'Gestion vulnérabilités DevSecOps', icon: '🥋', wizard: null, category: 'Vulnerability' },

                  // CI/CD & DevOps
                  { id: 'jenkins', name: 'Jenkins', description: 'Integration pipeline CI/CD', icon: '👷', wizard: null, category: 'DevOps' },
                  { id: 'gitlab', name: 'GitLab CI', description: 'Pipeline sécurité GitLab', icon: '🦊', wizard: null, category: 'DevOps' },
                  { id: 'github', name: 'GitHub Actions', description: 'Workflow sécurité GitHub', icon: '🐙', wizard: null, category: 'DevOps' },
                  { id: 'azure-devops', name: 'Azure DevOps', description: 'Pipeline Azure Pipelines', icon: '☁️', wizard: null, category: 'DevOps' },
                  { id: 'bitbucket', name: 'Bitbucket Pipelines', description: 'CI/CD Atlassian', icon: '🪣', wizard: null, category: 'DevOps' },

                  // Monitoring
                  { id: 'prometheus', name: 'Prometheus', description: 'Metriques et monitoring', icon: '📊', wizard: null, category: 'Monitoring' },
                  { id: 'grafana', name: 'Grafana', description: 'Tableaux de bord et visualisation', icon: '📈', wizard: null, category: 'Monitoring' },
                  { id: 'datadog', name: 'Datadog', description: 'Monitoring cloud complet', icon: '🐕', wizard: null, category: 'Monitoring' },
                  { id: 'newrelic', name: 'New Relic', description: 'Observabilite full-stack', icon: '📉', wizard: null, category: 'Monitoring' },
                  { id: 'zabbix', name: 'Zabbix', description: 'Monitoring infrastructure', icon: '📟', wizard: null, category: 'Monitoring' },
                  { id: 'nagios', name: 'Nagios', description: 'Monitoring systèmes et réseaux', icon: '🔔', wizard: null, category: 'Monitoring' },

                  // Cloud Storage
                  { id: 's3', name: 'AWS S3', description: 'Stockage cloud des rapports', icon: '☁️', wizard: null, category: 'Storage' },
                  { id: 'azure-blob', name: 'Azure Blob', description: 'Stockage Azure des rapports', icon: '💾', wizard: null, category: 'Storage' },
                  { id: 'gcs', name: 'Google Cloud Storage', description: 'Stockage GCP des rapports', icon: '🗄️', wizard: null, category: 'Storage' },
                  { id: 'minio', name: 'MinIO', description: 'Stockage S3-compatible on-premise', icon: '📦', wizard: null, category: 'Storage' },

                  // Event Streaming
                  { id: 'kafka', name: 'Apache Kafka', description: 'Streaming d\'evenements temps réel', icon: '🔄', wizard: null, category: 'Streaming' },
                  { id: 'rabbitmq', name: 'RabbitMQ', description: 'Message broker AMQP', icon: '🐰', wizard: null, category: 'Streaming' },
                  { id: 'redis', name: 'Redis Pub/Sub', description: 'Messaging temps réel Redis', icon: '🔴', wizard: null, category: 'Streaming' },

                  // Webhooks & API
                  { id: 'webhook', name: 'Webhook Generique', description: 'Webhook HTTP personnalise', icon: '🔗', wizard: 'webhook', category: 'API' },
                  { id: 'zapier', name: 'Zapier', description: 'Automatisation no-code', icon: '⚡', wizard: null, category: 'API' },
                  { id: 'n8n', name: 'n8n', description: 'Workflow automation open-source', icon: '🔀', wizard: null, category: 'API' },
                  { id: 'make', name: 'Make (Integromat)', description: 'Automatisation visuelle', icon: '🎨', wizard: null, category: 'API' },
                ];

                const handleIntegrationClick = (integration) => {
                  if (integration.wizard) {
                    setActiveWizard(integration.wizard);
                  } else {
                    setManualConfigModal(integration.id);
                  }
                };

                const handleWizardSave = (integrationId, config) => {
                  setIntegrations(prev => ({
                    ...prev,
                    [integrationId]: { ...config, enabled: true, configuréd_at: new Date().toISOString() }
                  }));
                  toast.success(`Intégration ${integrationId} configurée avec succès`);
                };

                // Group by category
                const categories = [...new Set(integrationsList.map(i => i.category))];

                return (
                  <div className="p-6">
                    <h2 className="text-xl font-semibold text-white mb-2 flex items-center gap-2">
                      <Plug className="w-5 h-5 text-brand-green" />
                      Integrations
                    </h2>
                    <p className="text-gray-400 mb-6">Connectez SYLINK a vos outils existants</p>

                    {/* Integration Stats */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-[#0d0d0d] rounded-xl p-4 border border-gray-800">
                        <div className="text-2xl font-bold text-white">{Object.keys(integrations).filter(k => integrations[k]?.enabled).length}</div>
                        <div className="text-sm text-gray-500">Actives</div>
                      </div>
                      <div className="bg-[#0d0d0d] rounded-xl p-4 border border-gray-800">
                        <div className="text-2xl font-bold text-white">{integrationsList.length}</div>
                        <div className="text-sm text-gray-500">Disponibles</div>
                      </div>
                      <div className="bg-[#0d0d0d] rounded-xl p-4 border border-gray-800">
                        <div className="text-2xl font-bold text-white">{categories.length}</div>
                        <div className="text-sm text-gray-500">Categories</div>
                      </div>
                    </div>

                    {/* API Section - Enterprise Only */}
                    {(() => {
                      const currentPlan = subscription?.plan || 'free';
                      const canUseAPI = ['enterprise', 'enterprise_plus', 'dedicated', 'groupe'].includes(currentPlan);

                      const apiEndpoints = [
                        { method: 'GET', path: '/api/v1/scan', description: 'Liste des scans', example: 'curl -H "Authorization: Bearer YOUR_API_KEY" https://sylink-pentest.fr/api/v1/scan' },
                        { method: 'POST', path: '/api/v1/scan', description: 'Lancer un nouveau scan', example: 'curl -X POST -H "Authorization: Bearer YOUR_API_KEY" -H "Content-Type: application/json" -d \'{"target":"192.168.1.0/24","workspace_id":"uuid"}\' https://sylink-pentest.fr/api/v1/scan' },
                        { method: 'GET', path: '/api/v1/scan/{id}', description: 'Details d\'un scan', example: 'curl -H "Authorization: Bearer YOUR_API_KEY" https://sylink-pentest.fr/api/v1/scan/{scan_id}' },
                        { method: 'GET', path: '/api/v1/scan/{id}/hosts', description: 'Hosts decouverts', example: 'curl -H "Authorization: Bearer YOUR_API_KEY" https://sylink-pentest.fr/api/v1/scan/{scan_id}/hosts' },
                        { method: 'GET', path: '/api/v1/scan/{id}/vulnerabilities', description: 'Vulnerabilites détectées', example: 'curl -H "Authorization: Bearer YOUR_API_KEY" https://sylink-pentest.fr/api/v1/scan/{scan_id}/vulnerabilities' },
                        { method: 'GET', path: '/api/v1/workspaces', description: 'Liste des workspaces', example: 'curl -H "Authorization: Bearer YOUR_API_KEY" https://sylink-pentest.fr/api/v1/workspaces' },
                        { method: 'GET', path: '/api/v1/vulnerabilities', description: 'Toutes les vulnérabilités', example: 'curl -H "Authorization: Bearer YOUR_API_KEY" https://sylink-pentest.fr/api/v1/vulnerabilities' },
                        { method: 'GET', path: '/api/v1/reports', description: 'Liste des rapports', example: 'curl -H "Authorization: Bearer YOUR_API_KEY" https://sylink-pentest.fr/api/v1/reports' },
                      ];

                      const copyToClipboard = (text, id) => {
                        navigator.clipboard.writeText(text);
                        setCopiedEndpoint(id);
                        setTimeout(() => setCopiedEndpoint(null), 2000);
                        toast.success('Copie dans le presse-papier');
                      };

                      return (
                        <div className="mb-8">
                          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                            <Key className="w-4 h-4" />
                            API REST
                          </h3>

                          <div className={`bg-[#0d0d0d] rounded-xl border ${canUseAPI ? 'border-purple-500/30' : 'border-gray-800'} overflow-hidden`}>
                            {/* API Header */}
                            <div className="p-5 border-b border-gray-800">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className={`w-12 h-12 rounded-xl ${canUseAPI ? 'bg-purple-500/20' : 'bg-gray-700'} flex items-center justify-center`}>
                                    <Key className={`w-6 h-6 ${canUseAPI ? 'text-purple-400' : 'text-gray-500'}`} />
                                  </div>
                                  <div>
                                    <h4 className="text-white font-semibold flex items-center gap-2">
                                      API SYLINK
                                      {canUseAPI ? (
                                        <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">Enterprise</span>
                                      ) : (
                                        <Lock className="w-4 h-4 text-gray-500" />
                                      )}
                                    </h4>
                                    <p className="text-sm text-gray-500">
                                      {canUseAPI
                                        ? 'Integrez SYLINK dans vos pipelines CI/CD et automatisations'
                                        : 'Disponible avec les plans Entreprise et Groupe'}
                                    </p>
                                  </div>
                                </div>
                                {canUseAPI && (
                                  <button
                                    onClick={() => setShowApiDocs(!showApiDocs)}
                                    className="px-4 py-2 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-lg hover:bg-purple-500/30 transition-colors text-sm flex items-center gap-2"
                                  >
                                    <FileText className="w-4 h-4" />
                                    {showApiDocs ? 'Masquer docs' : 'Documentation'}
                                  </button>
                                )}
                              </div>
                            </div>

                            {canUseAPI ? (
                              <>
                                {/* API Key Management */}
                                <div className="p-5 border-b border-gray-800">
                                  <div className="flex items-center justify-between mb-4">
                                    <h5 className="text-white font-medium">Votre clé API</h5>
                                    <button
                                      onClick={handleGenerateApiKey}
                                      className="px-4 py-2 bg-brand-green/20 text-brand-green border border-brand-green/50 rounded-lg hover:bg-brand-green/30 transition-colors text-sm flex items-center gap-2"
                                    >
                                      <RefreshCw className="w-4 h-4" />
                                      {apiKeyInfo?.has_key ? 'Regénérerr' : 'Générer'}
                                    </button>
                                  </div>

                                  {apiKeyInfo?.has_key ? (
                                    <div className="flex items-center gap-3">
                                      <div className="flex-1 bg-[#111111] rounded-lg px-4 py-3 font-mono text-sm text-gray-300 border border-gray-800">
                                        {apiKeyInfo.masked_key || '••••••••••••••••••••••••••••••••'}
                                      </div>
                                      <button
                                        onClick={() => copyToClipboard(apiKeyInfo.masked_key, 'apikey')}
                                        className="p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                                      >
                                        {copiedEndpoint === 'apikey' ? (
                                          <Check className="w-5 h-5 text-brand-green" />
                                        ) : (
                                          <Copy className="w-5 h-5 text-gray-400" />
                                        )}
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="bg-[#111111] rounded-lg px-4 py-3 text-gray-500 text-sm border border-gray-800">
                                      Aucune clé API générée. Cliquez sur "Générer" pour créer votre clé.
                                    </div>
                                  )}

                                  <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                                    <div className="flex items-start gap-2">
                                      <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                                      <p className="text-xs text-yellow-400">
                                        La clé API complète n'est affichée qu'une seule fois lors de la génération. Copiez-la immédiatement.
                                        La régénération invalidera l'ancienne clé.
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                {/* API Documentation */}
                                {showApiDocs && (
                                  <div className="p-5">
                                    <h5 className="text-white font-medium mb-4 flex items-center gap-2">
                                      <FileText className="w-4 h-4 text-purple-400" />
                                      Endpoints disponibles
                                    </h5>

                                    <div className="space-y-3">
                                      {apiEndpoints.map((endpoint, idx) => (
                                        <div key={idx} className="bg-[#111111] rounded-lg border border-gray-800 overflow-hidden">
                                          <div className="flex items-center justify-between p-3 border-b border-gray-800">
                                            <div className="flex items-center gap-3">
                                              <span className={`px-2 py-1 rounded text-xs font-bold ${
                                                endpoint.method === 'GET' ? 'bg-green-500/20 text-green-400' :
                                                endpoint.method === 'POST' ? 'bg-blue-500/20 text-blue-400' :
                                                endpoint.method === 'PUT' ? 'bg-yellow-500/20 text-yellow-400' :
                                                'bg-red-500/20 text-red-400'
                                              }`}>
                                                {endpoint.method}
                                              </span>
                                              <code className="text-sm text-gray-300 font-mono">{endpoint.path}</code>
                                            </div>
                                            <span className="text-xs text-gray-500">{endpoint.description}</span>
                                          </div>
                                          <div className="p-3 bg-[#0a0a0a]">
                                            <div className="flex items-center justify-between mb-2">
                                              <span className="text-xs text-gray-500">Exemple cURL</span>
                                              <button
                                                onClick={() => copyToClipboard(endpoint.example, idx)}
                                                className="text-xs text-gray-500 hover:text-brand-green flex items-center gap-1"
                                              >
                                                {copiedEndpoint === idx ? (
                                                  <>
                                                    <Check className="w-3 h-3" />
                                                    Copie!
                                                  </>
                                                ) : (
                                                  <>
                                                    <Copy className="w-3 h-3" />
                                                    Copier
                                                  </>
                                                )}
                                              </button>
                                            </div>
                                            <pre className="text-xs text-brand-green font-mono overflow-x-auto whitespace-pre-wrap">
                                              {endpoint.example}
                                            </pre>
                                          </div>
                                        </div>
                                      ))}
                                    </div>

                                    {/* Rate Limits Info */}
                                    <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                                      <h6 className="text-purple-400 font-medium mb-2 flex items-center gap-2">
                                        <Info className="w-4 h-4" />
                                        Limites et quotas
                                      </h6>
                                      <ul className="text-sm text-gray-400 space-y-1">
                                        <li>• Rate limit: 100 requetes/minute</li>
                                        <li>• Timeout: 30 secondes par requete</li>
                                        <li>• Format: JSON (Content-Type: application/json)</li>
                                        <li>• Authentification: Bearer token dans header Authorization</li>
                                      </ul>
                                    </div>

                                    {/* Swagger Link */}
                                    <div className="mt-4 flex items-center gap-3">
                                      <a
                                        href="/api/docs"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors text-sm flex items-center gap-2"
                                      >
                                        <ExternalLink className="w-4 h-4" />
                                        Documentation Swagger complete
                                      </a>
                                      <a
                                        href="/api/redoc"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors text-sm flex items-center gap-2"
                                      >
                                        <FileText className="w-4 h-4" />
                                        ReDoc
                                      </a>
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : (
                              /* Upgrade CTA for non-enterprise users */
                              <div className="p-5">
                                <div className="text-center py-6">
                                  <Lock className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                                  <h5 className="text-white font-medium mb-2">Accès API réservé aux plans Entreprise</h5>
                                  <p className="text-gray-500 text-sm mb-4 max-w-md mx-auto">
                                    L'API REST vous permet d'intégrer SYLINK dans vos workflows, pipelines CI/CD
                                    et outils d'automatisation.
                                  </p>
                                  <div className="flex items-center justify-center gap-3">
                                    <button
                                      onClick={() => navigate('/pricing')}
                                      className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm font-medium"
                                    >
                                      Passer a Entreprise
                                    </button>
                                    <button
                                      onClick={() => window.open('https://sylink-pentest.fr/contact', '_blank')}
                                      className="px-6 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors text-sm"
                                    >
                                      Nous contacter
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Integrations by Category */}
                    <div className="space-y-6">
                      {categories.map(category => (
                        <div key={category}>
                          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                            {category === 'SIEM' && '🔒'}
                            {category === 'Ticketing' && '🎫'}
                            {category === 'ChatOps' && '💬'}
                            {category === 'Notifications' && '🔔'}
                            {category === 'Monitoring' && '📊'}
                            {category === 'Storage' && '☁️'}
                            {category === 'Streaming' && '🔄'}
                            {category}
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {integrationsList.filter(i => i.category === category).map((integration) => {
                              const isConfigured = integrations[integration.id]?.enabled;
                              return (
                                <div
                                  key={integration.id}
                                  className={`bg-[#0d0d0d] rounded-xl p-4 border transition-colors ${
                                    isConfigured
                                      ? 'border-brand-green/50 bg-brand-green/5'
                                      : 'border-gray-800 hover:border-brand-green/30'
                                  }`}
                                >
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                      <span className="text-2xl">{integration.icon}</span>
                                      <div>
                                        <h3 className="font-semibold text-white flex items-center gap-2">
                                          {integration.name}
                                          {isConfigured && (
                                            <CheckCircle className="w-4 h-4 text-brand-green" />
                                          )}
                                        </h3>
                                        <p className="text-xs text-gray-500">{integration.description}</p>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleIntegrationClick(integration)}
                                      className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors ${
                                        isConfigured
                                          ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                          : 'bg-brand-green/20 text-brand-green border border-brand-green/50 hover:bg-brand-green/30'
                                      }`}
                                    >
                                      {isConfigured ? 'Modifier' : 'Configurer'}
                                    </button>
                                    {isConfigured && (
                                      <button
                                        onClick={() => {
                                          setIntegrations(prev => ({
                                            ...prev,
                                            [integration.id]: { ...prev[integration.id], enabled: false }
                                          }));
                                          toast.success(`Intégration ${integration.name} désactivée`);
                                        }}
                                        className="px-3 py-2 bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/30 text-sm"
                                      >
                                        Désactiver
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Wizards */}
                    {activeWizard === 'splunk' && (
                      <SplunkWizard
                        onClose={() => setActiveWizard(null)}
                        onSave={(id, config) => handleWizardSave(id, config)}
                      />
                    )}
                    {activeWizard === 'jira' && (
                      <JiraWizard
                        onClose={() => setActiveWizard(null)}
                        onSave={(id, config) => handleWizardSave(id, config)}
                      />
                    )}
                    {activeWizard === 'email' && (
                      <EmailWizard
                        onClose={() => setActiveWizard(null)}
                        onSave={(id, config) => handleWizardSave(id, config)}
                      />
                    )}
                    {['slack', 'teams', 'discord', 'mattermost'].includes(activeWizard) && (
                      <GenericWebhookWizard
                        platform={activeWizard}
                        onClose={() => setActiveWizard(null)}
                        onSave={(id, config) => handleWizardSave(id, config)}
                      />
                    )}

                    {/* Manual Config Modal */}
                    {manualConfigModal && (
                      <IntegrationManualConfigModal
                        integration={manualConfigModal}
                        isOpen={true}
                        onClose={() => setManualConfigModal(null)}
                      />
                    )}
                  </div>
                );
              })()}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (() => {
                // Determine plan capabilities
                const currentPlan = subscription?.plan || 'free';
                const canUseUI = true; // All plans
                const canUseEmail = ['pro', 'enterprise', 'enterprise_plus', 'dedicated', 'groupe'].includes(currentPlan);
                const canUseSMS = ['enterprise', 'enterprise_plus', 'dedicated', 'groupe'].includes(currentPlan);

                const notificationTypes = [
                  { id: 'scan_complete', label: 'Scan termine', description: 'Notification quand un scan est termine' },
                  { id: 'vuln_critical', label: 'Vulnerabilite critique', description: 'Alerte immediate pour les vulnérabilités critiques' },
                  { id: 'report_ready', label: 'Rapport disponible', description: 'Notification quand un rapport est généré' },
                  { id: 'weekly_summary', label: 'Résumé hebdomadaire', description: 'Synthèse de l\'activité de la semaine' },
                ];

                return (
                  <div className="p-6">
                    <h2 className="text-xl font-semibold text-white mb-2 flex items-center gap-2">
                      <Bell className="w-5 h-5 text-brand-green" />
                      Canaux de Notifications
                    </h2>
                    <p className="text-gray-400 mb-6">Choisissez comment recevoir vos alertes et notifications</p>

                    <div className="space-y-6">
                      {/* Channel Selection Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* UI Notifications */}
                        <div className={`bg-[#0d0d0d] rounded-xl p-5 border ${canUseUI ? 'border-brand-green/50' : 'border-gray-800 opacity-50'}`}>
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-brand-green/20 flex items-center justify-center">
                                <Bell className="w-5 h-5 text-brand-green" />
                              </div>
                              <div>
                                <h3 className="text-white font-medium">Interface (UI)</h3>
                                <p className="text-xs text-gray-500">Notifications dans l'app</p>
                              </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input type="checkbox" className="sr-only peer" defaultChecked disabled={!canUseUI} />
                              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-green"></div>
                            </label>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 bg-brand-green/20 text-brand-green text-xs rounded-full">
                              Tous les plans
                            </span>
                          </div>
                        </div>

                        {/* Email Notifications */}
                        <div className={`bg-[#0d0d0d] rounded-xl p-5 border ${canUseEmail ? 'border-blue-500/50' : 'border-gray-800 opacity-50'}`}>
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg ${canUseEmail ? 'bg-blue-500/20' : 'bg-gray-700'} flex items-center justify-center`}>
                                <Mail className={`w-5 h-5 ${canUseEmail ? 'text-blue-400' : 'text-gray-500'}`} />
                              </div>
                              <div>
                                <h3 className="text-white font-medium">Email</h3>
                                <p className="text-xs text-gray-500">Notifications par email</p>
                              </div>
                            </div>
                            {canUseEmail ? (
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" className="sr-only peer" defaultChecked />
                                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                              </label>
                            ) : (
                              <Lock className="w-5 h-5 text-gray-500" />
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {canUseEmail ? (
                              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                                Disponible
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded-full">
                                Pro et superieur
                              </span>
                            )}
                          </div>
                        </div>

                        {/* SMS Notifications */}
                        <div className={`bg-[#0d0d0d] rounded-xl p-5 border ${canUseSMS ? 'border-purple-500/50' : 'border-gray-800 opacity-50'}`}>
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg ${canUseSMS ? 'bg-purple-500/20' : 'bg-gray-700'} flex items-center justify-center`}>
                                <Smartphone className={`w-5 h-5 ${canUseSMS ? 'text-purple-400' : 'text-gray-500'}`} />
                              </div>
                              <div>
                                <h3 className="text-white font-medium">SMS</h3>
                                <p className="text-xs text-gray-500">Alertes par SMS</p>
                              </div>
                            </div>
                            {canUseSMS ? (
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" className="sr-only peer" />
                                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
                              </label>
                            ) : (
                              <Lock className="w-5 h-5 text-gray-500" />
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {canUseSMS ? (
                              <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                                Disponible
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded-full">
                                Entreprise et superieur
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Notification Types Configuration */}
                      <div className="bg-[#0d0d0d] rounded-xl p-5 border border-gray-800">
                        <h3 className="text-white font-medium mb-4">Types de notifications</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-gray-800">
                                <th className="text-left py-3 px-2 text-sm text-gray-400 font-medium">Notification</th>
                                <th className="text-center py-3 px-2 text-sm text-gray-400 font-medium">
                                  <div className="flex items-center justify-center gap-1">
                                    <Bell className="w-4 h-4" /> UI
                                  </div>
                                </th>
                                <th className="text-center py-3 px-2 text-sm text-gray-400 font-medium">
                                  <div className="flex items-center justify-center gap-1">
                                    <Mail className="w-4 h-4" /> Email
                                  </div>
                                </th>
                                <th className="text-center py-3 px-2 text-sm text-gray-400 font-medium">
                                  <div className="flex items-center justify-center gap-1">
                                    <Smartphone className="w-4 h-4" /> SMS
                                  </div>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {notificationTypes.map((notif) => (
                                <tr key={notif.id} className="border-b border-gray-800/50 last:border-0">
                                  <td className="py-3 px-2">
                                    <p className="text-white text-sm">{notif.label}</p>
                                    <p className="text-gray-500 text-xs">{notif.description}</p>
                                  </td>
                                  <td className="text-center py-3 px-2">
                                    <label className="relative inline-flex items-center cursor-pointer">
                                      <input type="checkbox" className="sr-only peer" defaultChecked />
                                      <div className="w-9 h-5 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-brand-green"></div>
                                    </label>
                                  </td>
                                  <td className="text-center py-3 px-2">
                                    {canUseEmail ? (
                                      <label className="relative inline-flex items-center cursor-pointer">
                                        <input type="checkbox" className="sr-only peer" defaultChecked={notif.id === 'vuln_critical'} />
                                        <div className="w-9 h-5 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-500"></div>
                                      </label>
                                    ) : (
                                      <Lock className="w-4 h-4 text-gray-600 mx-auto" />
                                    )}
                                  </td>
                                  <td className="text-center py-3 px-2">
                                    {canUseSMS ? (
                                      <label className="relative inline-flex items-center cursor-pointer">
                                        <input type="checkbox" className="sr-only peer" defaultChecked={notif.id === 'vuln_critical'} />
                                        <div className="w-9 h-5 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-500"></div>
                                      </label>
                                    ) : (
                                      <Lock className="w-4 h-4 text-gray-600 mx-auto" />
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Upgrade CTA if needed */}
                      {(!canUseEmail || !canUseSMS) && (
                        <div className="bg-gradient-to-r from-brand-green/10 to-blue-500/10 rounded-xl p-5 border border-brand-green/30">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-brand-green/20 flex items-center justify-center">
                              <Zap className="w-6 h-6 text-brand-green" />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-white font-medium">Debloquez plus de canaux de notification</h4>
                              <p className="text-gray-400 text-sm mt-1">
                                {!canUseEmail
                                  ? "Passez au plan Pro pour recevoir des notifications par email."
                                  : "Passez au plan Entreprise pour recevoir des alertes par SMS."}
                              </p>
                            </div>
                            <button
                              onClick={() => setActiveTab('subscription')}
                              className="px-4 py-2 bg-brand-green text-black font-medium rounded-lg hover:bg-brand-green/90 transition-colors"
                            >
                              Voir mon abonnement
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Mobile App Section */}
                      {(() => {
                        const canUseMobileApp = ['pro', 'enterprise', 'enterprise_plus', 'dedicated', 'groupe'].includes(currentPlan);

                        return (
                          <div className={`bg-gradient-to-br from-[#0d0d0d] to-[#1a1a2e] rounded-xl overflow-hidden border ${canUseMobileApp ? 'border-cyan-500/30' : 'border-gray-800'}`}>
                            <div className="p-6">
                              <div className="flex items-center gap-3 mb-4">
                                <div className={`w-12 h-12 rounded-xl ${canUseMobileApp ? 'bg-gradient-to-br from-cyan-500/20 to-purple-500/20' : 'bg-gray-700'} flex items-center justify-center`}>
                                  <Smartphone className={`w-6 h-6 ${canUseMobileApp ? 'text-cyan-400' : 'text-gray-500'}`} />
                                </div>
                                <div>
                                  <h3 className="text-white font-semibold text-lg">APPLICATION MOBILE</h3>
                                  <p className={`text-sm ${canUseMobileApp ? 'text-cyan-400' : 'text-gray-500'}`}>SYLINK PENTEST</p>
                                </div>
                                {canUseMobileApp ? (
                                  <span className="ml-auto px-3 py-1 bg-cyan-500/20 text-cyan-400 text-xs font-medium rounded-full">
                                    Disponible
                                  </span>
                                ) : (
                                  <span className="ml-auto px-3 py-1 bg-gray-700 text-gray-400 text-xs font-medium rounded-full flex items-center gap-1">
                                    <Lock className="w-3 h-3" /> Pro et superieur
                                  </span>
                                )}
                              </div>

                              <p className={`${canUseMobileApp ? 'text-gray-300' : 'text-gray-500'} text-sm leading-relaxed mb-6`}>
                                Gardez le contrôle de votre sécurité ou que vous soyez. Notre application mobile native vous offre une supervision en temps réel de vos audits, vous alertant instantanement de toute nouvelle vulnérabilité critique. Pilotez vos scans, consultez les rapports et gérez votre sécurité depuis votre poche.
                              </p>

                              {canUseMobileApp && (
                                <div className="flex flex-wrap gap-3 mb-6">
                                  <div className="flex items-center gap-2 px-3 py-1.5 bg-[#111]/50 rounded-lg">
                                    <Bell className="w-4 h-4 text-cyan-400" />
                                    <span className="text-gray-300 text-xs">Push notifications</span>
                                  </div>
                                  <div className="flex items-center gap-2 px-3 py-1.5 bg-[#111]/50 rounded-lg">
                                    <Shield className="w-4 h-4 text-cyan-400" />
                                    <span className="text-gray-300 text-xs">Alertes critiques</span>
                                  </div>
                                  <div className="flex items-center gap-2 px-3 py-1.5 bg-[#111]/50 rounded-lg">
                                    <FileText className="w-4 h-4 text-cyan-400" />
                                    <span className="text-gray-300 text-xs">Rapports PDF</span>
                                  </div>
                                  <div className="flex items-center gap-2 px-3 py-1.5 bg-[#111]/50 rounded-lg">
                                    <Zap className="w-4 h-4 text-cyan-400" />
                                    <span className="text-gray-300 text-xs">Lancement de scans</span>
                                  </div>
                                </div>
                              )}

                              {/* App Preview Image */}
                              {canUseMobileApp && (
                                <div
                                  className={`relative cursor-pointer transition-all duration-300 ${mobileAppImageZoomed ? 'fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-8' : ''}`}
                                  onClick={() => setMobileAppImageZoomed(!mobileAppImageZoomed)}
                                >
                                  <div className={`${mobileAppImageZoomed ? 'max-w-4xl w-full' : 'w-full'} transition-all duration-300`}>
                                    <img
                                      src="https://horizons-cdn.hostinger.com/62186562-3ea5-4175-8fff-73438c8a8ecf/bd0b3cce17ce52fba85df0ef4b8335f1.jpg"
                                      alt="SYLink Pentest Mobile App"
                                      className={`rounded-xl shadow-2xl transition-all duration-300 ${mobileAppImageZoomed ? 'max-h-[80vh] w-auto mx-auto' : 'w-full h-48 object-cover object-top'}`}
                                      style={{ objectPosition: mobileAppImageZoomed ? 'center' : 'top center' }}
                                    />
                                    {!mobileAppImageZoomed && (
                                      <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent rounded-xl flex items-end justify-center pb-4">
                                        <span className="text-gray-400 text-xs flex items-center gap-1">
                                          <Search className="w-3 h-3" /> Cliquez pour agrandir
                                        </span>
                                      </div>
                                    )}
                                    {mobileAppImageZoomed && (
                                      <div className="absolute top-4 right-4 text-white bg-black/50 px-3 py-1.5 rounded-lg text-sm">
                                        Cliquez pour fermer
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Download buttons */}
                              {canUseMobileApp && (
                                <div className="flex gap-3 mt-4">
                                  <a
                                    href="#"
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-black border border-gray-700 rounded-xl hover:border-gray-600 transition-colors"
                                  >
                                    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M17.5 12.5c0-1.58-.79-2.98-2-3.82V4.5h-7v4.18c-1.21.84-2 2.24-2 3.82 0 1.58.79 2.98 2 3.82V20h7v-3.68c1.21-.84 2-2.24 2-3.82zm-6 4.5c-2.49 0-4.5-2.01-4.5-4.5S9.01 8 11.5 8s4.5 2.01 4.5 4.5-2.01 4.5-4.5 4.5z"/>
                                    </svg>
                                    <div className="text-left">
                                      <p className="text-[10px] text-gray-400">Telecharger sur</p>
                                      <p className="text-white text-sm font-medium">App Store</p>
                                    </div>
                                  </a>
                                  <a
                                    href="#"
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-black border border-gray-700 rounded-xl hover:border-gray-600 transition-colors"
                                  >
                                    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                                    </svg>
                                    <div className="text-left">
                                      <p className="text-[10px] text-gray-400">Disponible sur</p>
                                      <p className="text-white text-sm font-medium">Google Play</p>
                                    </div>
                                  </a>
                                </div>
                              )}

                              {/* Upgrade CTA for non-eligible plans */}
                              {!canUseMobileApp && (
                                <button
                                  onClick={() => setActiveTab('subscription')}
                                  className="w-full mt-4 px-4 py-3 bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-medium rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                                >
                                  <Zap className="w-4 h-4" />
                                  Passer au plan Pro pour débloquer
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })()}

              {/* Version Tab */}
              {activeTab === 'version' && (
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                    <Info className="w-5 h-5 text-brand-green" />
                    Informations sur la version
                  </h2>

                  <div className="space-y-4">
                    {/* Current Version */}
                    <div className="bg-[#0d0d0d] rounded-xl p-5 border border-brand-green/50">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-white font-medium">SYLink Pentest</h4>
                          <p className="text-gray-500 text-sm">Plateforme autonome de tests d'intrusion</p>
                        </div>
                        <div className="text-right">
                          <span className="text-brand-green font-mono text-xl font-bold">v3.0.0</span>
                          <p className="text-brand-green/70 text-xs">Production - Janvier 2026</p>
                        </div>
                      </div>
                    </div>

                    {/* Components */}
                    <div className="bg-[#0d0d0d] rounded-xl p-5 border border-gray-800">
                      <h4 className="text-white font-medium mb-3">Composants</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-400">API Backend</span>
                          <span className="text-white font-mono">v3.0.0</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Interface Web</span>
                          <span className="text-white font-mono">v3.0.0</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Moteur de Scan</span>
                          <span className="text-white font-mono">v3.0.0</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Module IA</span>
                          <span className="text-white font-mono">v3.0.0</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Moteur OSINT</span>
                          <span className="text-white font-mono">v2.5.0</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Module d'Exploitation</span>
                          <span className="text-white font-mono">v2.0.0</span>
                        </div>
                      </div>
                    </div>

                    {/* Changelog */}
                    <div className="bg-[#0d0d0d] rounded-xl p-5 border border-gray-800">
                      <h4 className="text-white font-medium mb-4">Historique des versions</h4>

                      {/* v3.0.0 */}
                      <div className="mb-6 border-l-2 border-brand-green pl-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-brand-green font-mono font-bold">v3.0.0</span>
                          <span className="text-gray-500 text-xs">Janvier 2026</span>
                          <span className="px-2 py-0.5 bg-brand-green/20 text-brand-green text-xs rounded-full">Actuelle</span>
                        </div>
                        <ul className="text-gray-400 text-sm space-y-1">
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-brand-green mt-1 flex-shrink-0" /><span>Assistant IA conversationnel avec historique</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-brand-green mt-1 flex-shrink-0" /><span>Agent IA autonome avec supervision temps réel</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-brand-green mt-1 flex-shrink-0" /><span>Visual Attack avec coaching IA en direct</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-brand-green mt-1 flex-shrink-0" /><span>Techniques OCD AD Attacks 2025.03</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-brand-green mt-1 flex-shrink-0" /><span>Notifications multi-tenant isolées</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-brand-green mt-1 flex-shrink-0" /><span>Guide d'accueil interactif amélioré</span></li>
                        </ul>
                      </div>

                      {/* v2.8.0 */}
                      <div className="mb-6 border-l-2 border-gray-700 pl-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-white font-mono font-bold">v2.8.0</span>
                          <span className="text-gray-500 text-xs">Décembre 2025</span>
                        </div>
                        <ul className="text-gray-400 text-sm space-y-1">
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Recherche Dark Web par domaine et email</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>OSINT Breach Checker avec 300+ sites</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Intégration SpiderFoot pour OSINT passif</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Workers OSINT dédiés avec queue Redis</span></li>
                        </ul>
                      </div>

                      {/* v2.6.0 */}
                      <div className="mb-6 border-l-2 border-gray-700 pl-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-white font-mono font-bold">v2.6.0</span>
                          <span className="text-gray-500 text-xs">Novembre 2025</span>
                        </div>
                        <ul className="text-gray-400 text-sm space-y-1">
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Module APT avec simulations avancees</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Mapping MITRE ATT&CK complet</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Bypass EDR/AV avec techniques modernes</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Pivoting avec Chisel et Ligolo-ng</span></li>
                        </ul>
                      </div>

                      {/* v2.4.0 */}
                      <div className="mb-6 border-l-2 border-gray-700 pl-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-white font-mono font-bold">v2.4.0</span>
                          <span className="text-gray-500 text-xs">Octobre 2025</span>
                        </div>
                        <ul className="text-gray-400 text-sm space-y-1">
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Scans OT/ICS (Modbus, S7, DNP3, IEC-104)</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Scans IoT (MQTT, CoAP, UPnP, cameras)</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Audit Box/Firewall par marque</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Base CVE intégrée par équipement</span></li>
                        </ul>
                      </div>

                      {/* v2.2.0 */}
                      <div className="mb-6 border-l-2 border-gray-700 pl-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-white font-mono font-bold">v2.2.0</span>
                          <span className="text-gray-500 text-xs">Septembre 2025</span>
                        </div>
                        <ul className="text-gray-400 text-sm space-y-1">
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>VPN externe (Surfshark) et interne</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Support OpenVPN et WireGuard</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Isolation réseau par namespace</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Scans via tunnel VPN</span></li>
                        </ul>
                      </div>

                      {/* v2.0.0 */}
                      <div className="mb-6 border-l-2 border-gray-700 pl-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-white font-mono font-bold">v2.0.0</span>
                          <span className="text-gray-500 text-xs">Aout 2025</span>
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">Majeure</span>
                        </div>
                        <ul className="text-gray-400 text-sm space-y-1">
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Architecture multi-tenant avec workspaces</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>9 types de rapports (PDF, DOCX, HTML, Excel)</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Signature electronique des contrats</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Dashboard IA avec analyses predictives</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Support 6 langues (FR, EN, ES, DE, IT, PT)</span></li>
                        </ul>
                      </div>

                      {/* v1.8.0 */}
                      <div className="mb-6 border-l-2 border-gray-700 pl-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-white font-mono font-bold">v1.8.0</span>
                          <span className="text-gray-500 text-xs">Juin 2025</span>
                        </div>
                        <ul className="text-gray-400 text-sm space-y-1">
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Exploitation automatique des vulnérabilités</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Impacket (SecretsDump, PSExec, WMIExec)</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Attaques AD (Kerberoasting, Pass-the-Hash)</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Intégration Bloodhound pour cartographie AD</span></li>
                        </ul>
                      </div>

                      {/* v1.6.0 */}
                      <div className="mb-6 border-l-2 border-gray-700 pl-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-white font-mono font-bold">v1.6.0</span>
                          <span className="text-gray-500 text-xs">Avril 2025</span>
                        </div>
                        <ul className="text-gray-400 text-sm space-y-1">
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>SSO SAML 2.0 pour entreprises</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Authentification MFA</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Antivirus ClamAV intégré</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Quarantaine des fichiers malveillants</span></li>
                        </ul>
                      </div>

                      {/* v1.5.0 */}
                      <div className="mb-2 border-l-2 border-gray-700 pl-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-white font-mono font-bold">v1.5.0</span>
                          <span className="text-gray-500 text-xs">Fevrier 2025</span>
                          <span className="px-2 py-0.5 bg-gray-500/20 text-gray-400 text-xs rounded-full">Initiale</span>
                        </div>
                        <ul className="text-gray-400 text-sm space-y-1">
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Scans réseau et vulnérabilités</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>40+ outils de pentest intégrés</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Rapports PDF automatiques</span></li>
                          <li className="flex items-start gap-2"><CheckCircle className="w-3 h-3 text-gray-600 mt-1 flex-shrink-0" /><span>Système de crédits et abonnements</span></li>
                        </ul>
                      </div>
                    </div>

                    <div className="text-center text-gray-500 text-sm pt-4">
                      <p>&copy; 2026 SYLink. Tous droits réservés.</p>
                      <p className="mt-1">
                        <a href="https://sylink.fr" target="_blank" rel="noopener noreferrer" className="text-brand-green hover:underline">
                          sylink.fr
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Invite Team Member Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#0d0d0d] border border-gray-800 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-brand-green" />
              Inviter un membre
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">E-mail *</label>
                <input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                           focus:outline-none focus:border-brand-green/50"
                  placeholder="membre@exemple.fr"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Prénom</label>
                  <input
                    type="text"
                    value={inviteData.first_name}
                    onChange={(e) => setInviteData({ ...inviteData, first_name: e.target.value })}
                    className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                             focus:outline-none focus:border-brand-green/50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Nom</label>
                  <input
                    type="text"
                    value={inviteData.last_name}
                    onChange={(e) => setInviteData({ ...inviteData, last_name: e.target.value })}
                    className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                             focus:outline-none focus:border-brand-green/50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Role</label>
                <select
                  value={inviteData.role}
                  onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
                  className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white
                           focus:outline-none focus:border-brand-green/50"
                >
                  <option value="analyst">Analyste - Accès aux scans et rapports</option>
                  <option value="manager">Manager - Gestion des workspaces</option>
                  <option value="admin">Admin - Accès complet</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteData({ email: '', first_name: '', last_name: '', role: 'analyst' });
                }}
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-medium py-3 rounded-xl transition-all"
              >
                Annuler
              </button>
              <button
                onClick={handleInviteTeamMember}
                disabled={!inviteData.email || saving}
                className="flex-1 flex items-center justify-center gap-2 bg-brand-green hover:bg-brand-green/90 text-black font-semibold py-3 rounded-xl transition-all disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Envoyer l'invitation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credit Pack Purchase Modal */}
      {showCreditPackModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#0d0d0d] border border-gray-800 rounded-2xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Acheter des credits</h3>
                  <p className="text-gray-400 text-sm">Choisissez le pack qui correspond a vos besoins</p>
                </div>
              </div>
              <button
                onClick={() => setShowCreditPackModal(false)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Pack Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {creditPacks.map((pack) => (
                <div
                  key={pack.slug}
                  className={`relative bg-[#111111] rounded-xl p-5 border transition-all hover:scale-[1.02] ${
                    pack.popular
                      ? 'border-blue-500/50 ring-2 ring-blue-500/20'
                      : 'border-gray-800 hover:border-gray-700'
                  }`}
                >
                  {/* Popular Badge */}
                  {pack.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      POPULAIRE
                    </div>
                  )}

                  {/* Pack Name */}
                  <div className="text-center mb-4 mt-2">
                    <h4 className={`text-lg font-bold ${
                      pack.color === 'gray' ? 'text-gray-300' :
                      pack.color === 'blue' ? 'text-blue-400' :
                      pack.color === 'green' ? 'text-brand-green' :
                      'text-purple-400'
                    }`}>
                      {pack.name}
                    </h4>
                  </div>

                  {/* Credits Display */}
                  <div className="text-center mb-4">
                    <div className="text-3xl font-bold text-white">
                      {pack.credits}
                      {pack.bonus > 0 && (
                        <span className="text-brand-green text-lg ml-1">+{pack.bonus}</span>
                      )}
                    </div>
                    <div className="text-gray-500 text-sm">credits</div>
                  </div>

                  {/* Bonus Badge */}
                  {pack.bonus > 0 && (
                    <div className="flex items-center justify-center gap-1 mb-4">
                      <Gift className="w-4 h-4 text-brand-green" />
                      <span className="text-brand-green text-sm font-medium">
                        {pack.bonus} credits offerts !
                      </span>
                    </div>
                  )}

                  {/* Price */}
                  <div className="text-center mb-4">
                    <div className="text-2xl font-bold text-white">{pack.price}€</div>
                    <div className="text-gray-500 text-xs">
                      {((pack.price / (pack.credits + pack.bonus)) * 1).toFixed(2)}€ / credit
                    </div>
                  </div>

                  {/* Purchase Button */}
                  <button
                    onClick={() => handlePurchasePack(pack)}
                    disabled={purchasingPack === pack.slug}
                    className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                      pack.popular
                        ? 'bg-blue-500 hover:bg-blue-400 text-white'
                        : pack.color === 'green'
                        ? 'bg-brand-green hover:bg-brand-green/90 text-black'
                        : pack.color === 'purple'
                        ? 'bg-purple-500 hover:bg-purple-400 text-white'
                        : 'bg-gray-700 hover:bg-gray-600 text-white'
                    } disabled:opacity-50`}
                  >
                    {purchasingPack === pack.slug ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <ShoppingCart className="w-4 h-4" />
                        Acheter
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>

            {/* Info Footer */}
            <div className="bg-[#111111] rounded-xl p-4 border border-gray-800">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-400">
                  <p className="mb-2">
                    <strong className="text-white">Validite :</strong> Les credits achetes sont valables 12 mois (24 mois pour le pack Enterprise).
                  </p>
                  <p>
                    <strong className="text-white">Paiement securise :</strong> Tous les paiements sont traites de maniere securisee via Stripe.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MFA Setup Modal */}
      {showMfaModal && mfaSetup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#0d0d0d] border border-gray-800 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold text-white mb-4">Configuration MFA</h3>

            <p className="text-gray-400 text-sm mb-4">
              Scannez ce QR code avec Google Authenticator ou une application compatible
            </p>

            <div className="bg-white p-4 rounded-xl mb-4 flex justify-center">
              <img src={mfaSetup.qr_code} alt="QR Code" className="max-w-[180px]" />
            </div>

            {mfaSetup.backup_codes && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-4">
                <p className="text-yellow-400 text-sm font-medium mb-2">
                  Sauvegardez ces codes de secours :
                </p>
                <pre className="text-yellow-300 text-xs font-mono bg-black/20 p-2 rounded">
                  {mfaSetup.backup_codes.join('\n')}
                </pre>
              </div>
            )}

            <input
              type="text"
              placeholder="Code a 6 chiffres"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              className="w-full bg-[#111111] border border-gray-800 rounded-xl px-4 py-3 text-white text-center text-xl tracking-widest
                       focus:outline-none focus:border-brand-green/50 mb-4"
              maxLength={6}
            />

            <div className="flex gap-3">
              <button
                onClick={() => setShowMfaModal(false)}
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-medium py-3 rounded-xl transition-all"
              >
                Annuler
              </button>
              <button
                onClick={handleMfaEnable}
                disabled={mfaCode.length !== 6}
                className="flex-1 bg-brand-green hover:bg-brand-green/90 text-black font-semibold py-3 rounded-xl transition-all disabled:opacity-50"
              >
                Activer
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default Account;
